import { Fragment, useState, useEffect, useRef, useCallback, type ComponentProps } from "react"
import { saveBenefitChange, handledWorkspaceFailure } from "@/lib/benefit-save"
import { useBenefitReadBridge } from "@/features/benefit-workspace/useBenefitReadBridge"
import { benefitApi } from "@/features/benefit-workspace/benefitApi"
import { useBenefitScopeArg } from "@/features/benefit-workspace/useBenefitChildReads"
import { BenefitEditorWorkspace } from "@/features/benefit-workspace/BenefitEditorWorkspace"
import { useBenefitDraftState, useBenefitDraftRef, useBenefitSaving, useBenefitReviewActive } from "@/features/benefit-workspace/benefitEditorContext"
import type { GrantTask } from "@/features/benefit-workspace/benefitEditorDrafts"
import { captureGrantAuthoringContext, grantAuthoringContextCurrent } from "@/lib/grant-authoring-context"
import { captureEnergyBillContext, energyBillApplicantCurrent } from "@/lib/energy-editor-context"
import { useGrantView } from "@/features/benefit-workspace/useGrantView"
import type { GrantInformationTab } from "@/features/benefit-workspace/benefitEditorSlice"
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom"
import {
  IconArrowLeft,
  IconArrowUp,
  IconLoader2,
  IconUser,
  IconCurrencyDollar,
  IconFlame,
  IconBolt,
  IconDroplet,
  IconClipboardList,
  IconHistory,
  IconFileDescription,
  IconChevronDown,
  IconPlayerPlay,
  IconAlertTriangle,
  IconShield,
  IconEdit,
  IconPlus,
  IconTrash,
  IconChevronRight,
  IconInfoCircle,
  IconEye,
  IconCheck,
  IconClock,
  IconLock,
  IconShieldCheck,
} from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import "./grants-payments.css"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { ButtonGroup, ButtonGroupSeparator } from "@/components/ui/button-group"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { LifecycleActions } from "@/components/benefit/lifecycle-actions"
import { toast } from "sonner"
import { api, ApiError } from "@/lib/api"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import { formatPersonName } from "@/lib/person-identity"
import { useHasOpenBenefitEditors } from "@/components/benefit/benefit-edit-scope"
import { actionableGrantReview, captureGrantContext, captureGrantTaskBasis, currentGrantContext, grantAttempt, grantCapability, grantTaskAllowed, paymentPledgeLabel, unchangedGrantTask, usableGrantAuthority, validGrantDate, validReplacement as validReplacementInput, validRequiredReason, type GrantCapabilityKind, type GrantEditContext, type GrantTaskName } from "@/lib/grant-workspace"
import { useAppSelector } from "@/store"
import {
  NON_CASH_BENEFIT_OPTIONS,
  OTHER_NON_CASH_BENEFIT,
  hasNonCashBenefit,
  setNonCashBenefit,
} from "@/lib/non-cash-benefits"
import { parseQuestionnaireAmount, questionnaireAnswers } from "@/lib/questionnaire"
import { normalizeEnergyBillSsn } from "@/lib/energy-bill"
import type { EnergyServiceOption } from "@/lib/energy-intake"
import {
  availableGrantEligibility,
  grantDefaults,
  grantInstanceKey,
  isGrantEntryEditable,
} from "@/lib/grant-options"
import { vendorPaymentOptions } from "@/lib/vendor-payments"
import { captureVendorPaymentContext, vendorPaymentContextCurrent } from "@/lib/vendor-payment-context"
import { useGrantRemoval } from "@/components/benefit/use-grant-removal"
import { parseLiheapVendorSlots } from "@/lib/liheap-vendor-payments"
import { BENEFIT_SECTION_LABELS, BENEFIT_SECTION_TABS, benefitSectionTab, benefitHistoryScope, withBenefitHistoryScope, withBenefitSection } from "@/lib/benefit-section-tabs"
import { deliveryReadbackDisplay, runCorrectionAction, workflowCanEdit, workflowReasonGroup } from "@/lib/benefit-workflow"
import {
  benefitAppTypeLabels,
  formatServiceLabel,
  getBenefitProgramLabel,
  heatingEnergyLabels,
  outcomeLabels,
  pseAppearanceOnBillLabels,
  pseApplicationTypeLabels,
  pseEnergyTypeLabels,
  twoYearCertStateLabels,
} from "@/lib/benefit-enums"
import {
  boolLabel,
  enumLabel,
  fmt,
  fmtDate,
  fmtDateTime,
  fmtMoney,
  fmtPhone,
  fmtSsn,
  formatAddress,
  isBenefitEditable,
} from "@/lib/benefit-format"
import type {
  BenefitDetailDto,
  BenefitEligibilityDto,
  BenefitGrantDto,
  BenefitGrantReview,
  BenefitPledgeGeneration,
  BenefitWorkflowResponse,
  LiheapBenefitDto,
  ProvidedServiceDto,
  PseBenefitDto,
  SelectableGrantProgramDto,
  ServiceProviderPaymentDto,
  SheapBenefitDto,
  VendorPaymentVendorDto,
} from "@/lib/benefit-types"
import {
  EditableCardActions,
  Field,
  FieldGrid,
  GrantStatusBadge,
  WorkflowStatusBadge,
} from "@/components/benefit/field"
import { ApplicationDetailsSection } from "@/components/benefit/application-details-section"
import { EnergyIntakeSection } from "@/components/benefit/energy-intake-section"
import { programHeatingSourceDisplay } from "@/lib/primary-heat-display"
import { RequestedBenefitsSection } from "@/components/benefit/requested-benefits-section"
import { EligibilityCalculationSection } from "@/components/benefit/eligibility-calculation-section"
import { IntakeDetailsSection } from "@/components/benefit/intake-details-section"
import { BenefitPrintActions } from "@/components/benefit/benefit-print-actions"
import { GrantOutcomes } from "@/components/benefit/grant-outcomes"
import { HouseholdCopyNotice } from "@/components/benefit/household-copy-notice"
import { PseBackupCostDetails } from "@/components/benefit/pse-backup-cost-details"
import { canCorrectDraftGrant } from "@/lib/grant-correction"
import { HouseholdInformationSection } from "@/components/benefit/household-information-section"
import { HouseholdIncomeSection } from "@/components/benefit/household-income-section"
import { ApplicantHouseholdCorrection } from "@/components/benefit/applicant-household-correction"
import { HouseholdMembersSection } from "@/components/benefit/household-members-section"
import { DemographicsDetails } from "@/components/benefit/demographics-details"
import { VendorPaymentFields } from "@/components/benefit/vendor-payment-fields"
import { BenefitOwnershipCard } from "@/components/benefit/ownership-card"
import { ApplicationHistory } from "@/components/benefit/application-history"
import { ApplicationOverview } from "@/components/benefit/application-overview"
import { HouseholdDetails } from "@/components/benefit/household-details"
import { householdEditDisabledReason } from "@/lib/household-edit-availability"
import { ProgramDetails } from "@/components/benefit/program-details"
import { billIdentityOf } from "@/lib/bill-identity"
import { canReassignBenefitOwnership, ownerDisplay } from "@/lib/benefit-ownership"
import { TargetProgramYearSection } from "@/components/benefit/target-program-year-section"


// ── Section Components ────────────────────────────────────────────────

// Recorded application details (#474), retained in Overview's read-only detail sheet.
// The compact overview is composed separately; these existing fields remain available:
//   1. Applicant Name + SSN
//   2. Secondary Applicant
//   3. Residential + Mailing Address
//   4. Contact Information
//   5. Application Date + Certification Date (only these two)
//   6. Average Monthly Income
//   7. Number of Household Members
//   8. Number of Persons with LIHEAP Eligibility
// Admin metadata (Benefit ID, File Number, Intake Date, Intake Type, Intake Location,
// Staff, Last Changed, Incorporation, Primary Service Provider) moves to the new
// AdministrativeDataSection below.
function RecordedApplicationDetails({ b, workflow }: { b: BenefitDetailDto; workflow: BenefitWorkflowResponse | null }) {
  const applicant = b.personCollection.find((p) => p.relationship === 0)
  const secondary = b.secondaryId
    ? b.personCollection.find((p) => p.id === b.secondaryId)
    : undefined
  const residential = b.addresses?.["Residential"] ?? b.addresses?.["1"] ?? b.addresses?.["0"]
  const mailing = b.addresses?.["Mailing"] ?? b.addresses?.["2"]
  const liheapEligibleCount = b.personCollection.filter(
    (p) => p.eligibilityStatus === 1,
  ).length
  const applicantName = applicant ? formatPersonName(applicant) : "—"
  const secondaryName = secondary ? formatPersonName(secondary) : "—"
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconFileDescription className="size-5" />
            <CardTitle className="text-base">Application facts</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <WorkflowStatusBadge workflow={workflow} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldGrid cols={2}>
          <Field label="Applicant" value={applicantName} />
          <Field label="Applicant SSN" value={<span className="font-mono text-xs">{fmtSsn(applicant?.socialSecurityNumber)}</span>} />
          <Field label="Secondary Applicant" value={secondaryName} />
          <Field label="Secondary Applicant SSN" value={<span className="font-mono text-xs">{fmtSsn(secondary?.socialSecurityNumber)}</span>} />
        </FieldGrid>
        <Separator />
        <FieldGrid cols={2}>
          <Field label="Residential Address" value={formatAddress(residential)} />
          <Field label="Mailing Address" value={formatAddress(mailing)} />
        </FieldGrid>
        <Separator />
        <FieldGrid cols={3}>
          <Field label="Home Phone" value={fmtPhone(b.homePhone)} />
          <Field label="Message Phone" value={fmtPhone(b.messagePhone)} />
          <Field label="Contact Email" value={fmt(b.contactEmail)} />
        </FieldGrid>
        <Separator />
        <FieldGrid>
          <Field label="Application Date" value={fmtDate(b.applicationDate)} />
          <Field label="Certification Date" value={fmtDate(b.certificationDate)} />
          <Field label="Average Monthly Income" value={fmtMoney(b.monthlyAdjustedIncome)} />
          <Field label="Household Members" value={fmt(b.peopleInHousehold)} />
          <Field label="LIHEAP-Eligible Persons" value={String(liheapEligibleCount)} />
        </FieldGrid>
      </CardContent>
    </Card>
  )
}

// Administrative Data block (#475) — admin metadata moved out of Overview per #474.
// Renders directly above Application History.
//
// Field set per Amanda's spec: Intake Type, Intake Location, current grant outcomes,
// Application Status, Pledge Status, Benefit ID, File Number. Outcomes are read from the
// current per-grant collection; the legacy application-scoped fields are not an authority.
//
// Pledge Status is intentionally rendered as "—": there's no PledgeStatus field on the
// benefit yet (the only pledge tracking lives on PseOnlineCsvRecords). When a dedicated
// pledge field is added to BenefitDto the rendering here is the only update needed.
function AdministrativeDataSection({
  b,
  workflow,
  providerNames,
  energyServiceOptions,
}: {
  b: BenefitDetailDto
  workflow: BenefitWorkflowResponse | null
  providerNames: Record<string, string>
  energyServiceOptions: readonly EnergyServiceOption[]
}) {
  const heatingSources = programHeatingSourceDisplay(b, providerNames, energyServiceOptions)
  const liheapPrimaryProviderName = heatingSources.liheapPrimaryServiceProvider
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <IconShield className="size-5" />
          <CardTitle className="text-base">Administrative Data</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldGrid>
          <Field label="Benefit ID" value={<span className="font-mono text-xs">{b.id}</span>} />
          <Field label="File Number" value={fmt(b.fileNumber || null)} />
          <Field label="Intake Date" value={fmtDateTime(b.intakeDate)} />
          <Field label="Last Changed" value={fmtDateTime(b.changeDate)} />
          <Field label="Intake Type" value={fmt(b.intakeType)} />
          <Field label="Intake Location" value={fmt(b.intakeLocation)} />
          <Field label="Intake owner" value={ownerDisplay(b.ownership)} />
          <Field label="Legacy staff label" value={fmt(b.staff)} />
          {liheapPrimaryProviderName && <Field label="LIHEAP Primary Service Provider" value={liheapPrimaryProviderName} />}
        </FieldGrid>
        <Separator />
        <FieldGrid>
          <Field label="Application Status" value={<WorkflowStatusBadge workflow={workflow} />} />
          <Field
            label="Grant outcomes"
            value={<GrantOutcomes grants={b.benefitGrants} />}
          />
          {/* #475: Pledge Status — no first-class field on BenefitDto yet; deferred. */}
          <Field label="Pledge Status" value="—" />
        </FieldGrid>
      </CardContent>
    </Card>
  )
}

// Floating back-to-top button (#473). Visible after the user scrolls past one viewport
// height. Single discoverable control reachable from every block on the long Benefit
// Application page; satisfies the "jump to top" intent without cluttering each card header.
function BackToTopButton() {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.4)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])
  if (!visible) return null
  return (
    <Button
      size="icon"
      variant="secondary"
      className="fixed bottom-6 right-6 z-50 size-10 rounded-full shadow-lg border"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      title="Back to top"
    >
      <IconArrowUp className="size-5" />
    </Button>
  )
}

// #487 — color-code Amount Remaining: negative red, positive green, zero default.
// Tailwind classes chosen to meet WCAG AA on both light and dark backgrounds.
function AmountRemaining({ value }: { value: number | null | undefined }) {
  const text = fmtMoney(value)
  if (value === null || value === undefined || value === 0) {
    return <span>{text}</span>
  }
  const color = value < 0
    ? "text-red-600 dark:text-red-400"
    : "text-green-700 dark:text-green-400"
  return <span className={`font-medium ${color}`}>{text}</span>
}

export function NonCashBenefitsSection({
  benefit,
  canEdit,
  onUpdated,
}: {
  benefit: BenefitDetailDto
  canEdit: boolean
  onUpdated: (updated: BenefitDetailDto) => void
}) {
  const [isEditing, setIsEditing, editToken] = useBenefitEdit(benefit.concurrencyToken, "non-cash")
  const [pending, setIsSaving] = useBenefitSaving()
  const reviewing = useBenefitReviewActive()
  const isSaving = pending || reviewing
  const [mask, setMask] = useBenefitDraftState("non-cash", "mask", benefit.otherCashBenefits)
  const [receivedFoodStamps, setReceivedFoodStamps] = useBenefitDraftState("non-cash", "receivedFoodStamps", benefit.recvdFoodStamps)
  const [otherDescription, setOtherDescription] = useBenefitDraftState("non-cash", "otherDescription",
    benefit.otherNonCashBenefitsDescription ?? "",
  )

  const resetForm = () => {
    setMask(benefit.otherCashBenefits)
    setReceivedFoodStamps(benefit.recvdFoodStamps)
    setOtherDescription(benefit.otherNonCashBenefitsDescription ?? "")
  }

  const beginEdit = () => {
    if (!canEdit) return
    resetForm()
    setIsEditing(true)
  }

  const cancelEdit = () => setIsEditing(false)

  const save = async () => {
    if (!isEditing || isSaving || !canEdit) return
    if (hasNonCashBenefit(mask, OTHER_NON_CASH_BENEFIT) && !otherDescription.trim()) {
      toast.error("Describe the other non-cash benefit before saving.")
      return
    }

    setIsSaving(true)
    try {
      const result = await saveBenefitChange(benefit.id, onUpdated, () => api.patch<BenefitDetailDto>(
        `/api/benefitapplications/${benefit.id}/non-cash-benefits`,
        {
          concurrencyToken: editToken,
          otherCashBenefits: mask,
          recvdFoodStamps: receivedFoodStamps,
          otherDescription: hasNonCashBenefit(mask, OTHER_NON_CASH_BENEFIT)
            ? otherDescription.trim()
            : null,
        },
      ), "Non-cash benefits updated.")
      if (result.saved || result.reloaded) setIsEditing(false)
    } finally {
      setIsSaving(false)
    }
  }

  const selected = NON_CASH_BENEFIT_OPTIONS.filter((option) =>
    hasNonCashBenefit(benefit.otherCashBenefits, option.value),
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconClipboardList className="size-5" />
            <div>
              <CardTitle className="text-base">Non-Cash Benefits</CardTitle>
              <CardDescription>Food and housing assistance received by the household</CardDescription>
            </div>
          </div>
          <EditableCardActions
            isEditing={isEditing}
            isSaving={isSaving}
            canEdit={canEdit}
            saveDisabled={!canEdit}
            restorePanelFocus
            onEdit={beginEdit}
            onSave={save}
            onCancel={cancelEdit}
          />
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={receivedFoodStamps}
                  disabled={isSaving}
                  onCheckedChange={(checked) => setReceivedFoodStamps(checked === true)}
                />
                Food Stamps (SNAP)
              </label>
              {NON_CASH_BENEFIT_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={hasNonCashBenefit(mask, option.value)}
                    disabled={isSaving}
                    onCheckedChange={(checked) =>
                      setMask((current) => setNonCashBenefit(current, option.value, checked === true))
                    }
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {hasNonCashBenefit(mask, OTHER_NON_CASH_BENEFIT) && (
              <div className="max-w-xl space-y-1.5">
                <label htmlFor="other-non-cash-description" className="text-sm font-medium">
                  Other benefit description
                </label>
                <Input
                  id="other-non-cash-description"
                  maxLength={250}
                  value={otherDescription}
                  disabled={isSaving}
                  onChange={(event) => setOtherDescription(event.target.value)}
                  placeholder="Describe the other non-cash benefit"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {benefit.recvdFoodStamps && <Badge>Food Stamps (SNAP)</Badge>}
            {selected.map((option) => (
              <Badge key={option.value} variant="secondary">
                {option.label}
                {option.value === OTHER_NON_CASH_BENEFIT && benefit.otherNonCashBenefitsDescription
                  ? `: ${benefit.otherNonCashBenefitsDescription}`
                  : ""}
              </Badge>
            ))}
            {!benefit.recvdFoodStamps && selected.length === 0 && (
              <span className="text-sm text-muted-foreground">None reported</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function FinancialSection({ b }: { b: BenefitDetailDto }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <IconCurrencyDollar className="size-5" />
          <CardTitle className="text-base">Financial Summary</CardTitle>
        </div>
        <CardDescription>Authored grants are distinct from legacy processing and disbursement totals.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldGrid cols={2}>
          <Field label="Awarded Grant Total" value={fmtMoney(b.benefitGrants.reduce((sum, grant) => sum + grant.grantAmount, 0))} />
          <Field label="Grants" value={String(b.benefitGrants.length)} />
        </FieldGrid>
        <Separator />
        <p className="text-xs text-muted-foreground">The processing totals below are not changed by intake, grant entry, or application submission. No payment is issued by those actions.</p>
        <FieldGrid>
          <Field label="Amount Eligible (Processing)" value={fmtMoney(b.amountEligible)} />
          <Field label="Amount Paid" value={fmtMoney(b.amountPaid)} />
          <Field
            label="Amount Unused"
            value={<AmountRemaining value={b.amountUnused} />}
          />
        </FieldGrid>
        <Separator />
        <FieldGrid>
          <Field label="2-Year Certification" value={boolLabel(b.isEligibleForTwoYearCertification)} />
          <Field label="2-Year Cert State" value={enumLabel(twoYearCertStateLabels, b.twoYearCertificationState)} />
        </FieldGrid>
      </CardContent>
    </Card>
  )
}

export function QuestionnaireSection({
  benefit,
  canEdit,
  onUpdated,
}: {
  benefit: BenefitDetailDto
  canEdit: boolean
  onUpdated: (updated: BenefitDetailDto) => void
}) {
  const [isEditing, setIsEditing, editToken] = useBenefitEdit(benefit.concurrencyToken, "questionnaire")
  const [pending, setIsSaving] = useBenefitSaving()
  const reviewing = useBenefitReviewActive()
  const isSaving = pending || reviewing
  const answers = questionnaireAnswers(benefit)
  const [pseEnabled, setPseEnabled] = useBenefitDraftState("questionnaire", "pseEnabled", answers.pse != null)
  const [liheapEnabled, setLiheapEnabled] = useBenefitDraftState("questionnaire", "liheapEnabled", answers.liheap != null)
  const [sheapEnabled, setSheapEnabled] = useBenefitDraftState("questionnaire", "sheapEnabled", answers.sheap != null)
  const [weatherization, setWeatherization] = useBenefitDraftState("questionnaire", "weatherization", benefit.weatherization)
  const [pastDueForgiveness, setPastDueForgiveness] = useBenefitDraftState("questionnaire", "pastDueForgiveness", answers.pse?.interestedInPastDueForgivenessProgram ?? false)
  const [lifeThreateningCrisis, setLifeThreateningCrisis] = useBenefitDraftState("questionnaire", "lifeThreateningCrisis", answers.liheap?.lifeThreateningEnergyCrisis ?? null)
  const [furnaceOlder, setFurnaceOlder] = useBenefitDraftState("questionnaire", "furnaceOlder", answers.sheap?.furnaceOlderThanFiveYears ?? false)
  const [furnaceHeatsHome, setFurnaceHeatsHome] = useBenefitDraftState("questionnaire", "furnaceHeatsHome", answers.sheap?.furnaceUsedToHeatHome ?? false)
  const [rentalPastDue, setRentalPastDue] = useBenefitDraftState("questionnaire", "rentalPastDue", String(answers.liheap?.rentalPastDue ?? 0))
  const [waterPastDue, setWaterPastDue] = useBenefitDraftState("questionnaire", "waterPastDue", String(answers.liheap?.waterPastDue ?? 0))
  const [coolingCost, setCoolingCost] = useBenefitDraftState("questionnaire", "coolingCost", String(answers.liheap?.coolingCost ?? 0))

  const resetForm = () => {
    setPseEnabled(answers.pse != null)
    setLiheapEnabled(answers.liheap != null)
    setSheapEnabled(answers.sheap != null)
    setWeatherization(benefit.weatherization)
    setPastDueForgiveness(answers.pse?.interestedInPastDueForgivenessProgram ?? false)
    setLifeThreateningCrisis(answers.liheap?.lifeThreateningEnergyCrisis ?? null)
    setFurnaceOlder(answers.sheap?.furnaceOlderThanFiveYears ?? false)
    setFurnaceHeatsHome(answers.sheap?.furnaceUsedToHeatHome ?? false)
    setRentalPastDue(String(answers.liheap?.rentalPastDue ?? 0))
    setWaterPastDue(String(answers.liheap?.waterPastDue ?? 0))
    setCoolingCost(String(answers.liheap?.coolingCost ?? 0))
  }

  const save = async () => {
    if (!isEditing || isSaving || !canEdit) return
    const parsedRentalPastDue = parseQuestionnaireAmount(rentalPastDue)
    const parsedWaterPastDue = parseQuestionnaireAmount(waterPastDue)
    const parsedCoolingCost = parseQuestionnaireAmount(coolingCost)
    if (
      liheapEnabled &&
      (parsedRentalPastDue === null || parsedWaterPastDue === null || parsedCoolingCost === null)
    ) {
      toast.error("Past-due and cooling amounts must be valid non-negative numbers.")
      return
    }

    setIsSaving(true)
    try {
      const result = await saveBenefitChange(benefit.id, onUpdated, () => api.patch<BenefitDetailDto>(
        `/api/benefitapplications/${benefit.id}/questionnaire`,
        {
          concurrencyToken: editToken,
          interestedInWeatherization: weatherization,
          pse: pseEnabled
            ? { interestedInPastDueForgivenessProgram: pastDueForgiveness }
            : null,
          liheap: liheapEnabled
            ? {
                lifeThreateningEnergyCrisis: lifeThreateningCrisis,
                rentalPastDue: parsedRentalPastDue!,
                waterPastDue: parsedWaterPastDue!,
                coolingCost: parsedCoolingCost!,
              }
            : null,
          sheap: sheapEnabled
            ? {
                furnaceOlderThanFiveYears: furnaceOlder,
                furnaceUsedToHeatHome: furnaceHeatsHome,
              }
            : null,
        },
      ), "Questionnaire updated.")
      if (result.saved || result.reloaded) setIsEditing(false)
    } finally {
      setIsSaving(false)
    }
  }

  const checkboxSignal = (
    label: string,
    checked: boolean,
    onCheckedChange: (checked: boolean) => void,
    available: boolean,
  ) => (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox
        checked={checked}
        disabled={isSaving || !available}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
      <span>
        {label}
        {!available && <span className="ml-1 text-xs text-muted-foreground">(not available)</span>}
      </span>
    </label>
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconClipboardList className="size-5" />
            <div>
              <CardTitle className="text-base">Questionnaire</CardTitle>
              <CardDescription>Eligibility and program-interest signals</CardDescription>
            </div>
          </div>
          <EditableCardActions
            isEditing={isEditing}
            isSaving={isSaving}
            canEdit={canEdit}
            saveDisabled={!canEdit}
            restorePanelFocus
            onEdit={() => {
              if (!canEdit) return
              resetForm()
              setIsEditing(true)
            }}
            onSave={save}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <p className="text-xs text-muted-foreground">Supplementary answers can be recorded before requesting assistance. They do not enroll a program.</p>
              <div className="flex flex-wrap gap-2">
                {!pseEnabled && <Button variant="outline" size="sm" disabled={isSaving} onClick={() => setPseEnabled(true)}>Record PSE answers</Button>}
                {!liheapEnabled && <Button variant="outline" size="sm" disabled={isSaving} onClick={() => setLiheapEnabled(true)}>Record LIHEAP answers</Button>}
                {!sheapEnabled && <Button variant="outline" size="sm" disabled={isSaving} onClick={() => setSheapEnabled(true)}>Record SHEAP answers</Button>}
              </div>
            </div>
            {checkboxSignal(
              "Interested in Past Due Forgiveness Program",
              pastDueForgiveness,
              setPastDueForgiveness,
              pseEnabled,
            )}
            {checkboxSignal("Interested in Weatherization", weatherization, setWeatherization, true)}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Life-Threatening Energy Crisis</label>
              <Select
                value={lifeThreateningCrisis === null ? "unanswered" : lifeThreateningCrisis ? "yes" : "no"}
                onValueChange={(value) =>
                  setLifeThreateningCrisis(value === "unanswered" ? null : value === "yes")
                }
                disabled={isSaving || !liheapEnabled}
              >
                <SelectTrigger aria-label="Life-Threatening Energy Crisis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unanswered">Not answered</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {checkboxSignal(
              "Furnace older than 5 years",
              furnaceOlder,
              setFurnaceOlder,
              sheapEnabled,
            )}
            {checkboxSignal(
              "Furnace used to heat the home",
              furnaceHeatsHome,
              setFurnaceHeatsHome,
              sheapEnabled,
            )}
            {[
              ["Rental Past Due", rentalPastDue, setRentalPastDue],
              ["Water Past Due", waterPastDue, setWaterPastDue],
              ["Cooling Cost", coolingCost, setCoolingCost],
            ].map(([label, value, setter]) => (
              <div key={label as string} className="space-y-1.5">
                <label className="text-sm font-medium">{label as string}</label>
                <Input
                  aria-label={label as string}
                  type="text"
                  inputMode="decimal"
                  value={value as string}
                  disabled={isSaving || !liheapEnabled}
                  onChange={(event) => (setter as (amount: string) => void)(event.target.value)}
                />
              </div>
            ))}
          </div>
        ) : (
          <FieldGrid>
            <Field
              label="Interested in Past Due Forgiveness Program"
              value={boolLabel(answers.pse?.interestedInPastDueForgivenessProgram)}
            />
            <Field label="Interested in Weatherization" value={boolLabel(benefit.weatherization)} />
            <Field
              label="Life-Threatening Energy Crisis"
              value={boolLabel(answers.liheap?.lifeThreateningEnergyCrisis)}
            />
            <Field
              label="Furnace older than 5 years"
              value={boolLabel(answers.sheap?.furnaceOlderThanFiveYears)}
            />
            <Field
              label="Furnace used to heat the home"
              value={boolLabel(answers.sheap?.furnaceUsedToHeatHome)}
            />
            <Field label="Rental Past Due" value={fmtMoney(answers.liheap?.rentalPastDue)} />
            <Field label="Water Past Due" value={fmtMoney(answers.liheap?.waterPastDue)} />
            <Field label="Cooling Cost" value={fmtMoney(answers.liheap?.coolingCost)} />
          </FieldGrid>
        )}
      </CardContent>
    </Card>
  )
}

export function EnergyBillInformationSection({
  benefit,
  providerNames,
  canEdit,
  onUpdated,
  energyServiceOptions,
}: {
  benefit: BenefitDetailDto
  providerNames: Record<string, string>
  canEdit: boolean
  onUpdated: (updated: BenefitDetailDto) => void
  energyServiceOptions?: readonly EnergyServiceOption[]
}) {
  const energyRead = benefitApi.useGetBenefitEnergyServicesQuery(useBenefitScopeArg(energyServiceOptions === undefined))
  const resolvedEnergyServiceOptions = energyServiceOptions ?? energyRead.currentData ?? []
  const bill = benefit.pseBenefitDetails?.pseBillInformation
  const sharedBill = benefit.energyBillInformation
  // Shared projection first, legacy PSE bill as the fallback (including its permission-gated SSN).
  const billIdentity = billIdentityOf(benefit)
  const [isEditing, setIsEditing, editToken] = useBenefitEdit(benefit.concurrencyToken, "energy-bill")
  const [pending, setIsSaving] = useBenefitSaving()
  const reviewing = useBenefitReviewActive()
  const isSaving = pending || reviewing
  const [identityContext, setIdentityContext] = useBenefitDraftState("energy-bill", "identityContext", () => captureEnergyBillContext(benefit))
  const hasPseSection = isEditing ? identityContext.hasPseSection : benefit.pseBenefitDetails != null
  const [appearance, setAppearance] = useBenefitDraftState("energy-bill", "appearance",
    (sharedBill?.applicantAppearsAs ?? benefit.pseBenefitDetails?.applicantsAppearanceOnPseBill) == null
      ? "unanswered"
      : String(sharedBill?.applicantAppearsAs ?? benefit.pseBenefitDetails?.applicantsAppearanceOnPseBill),
  )
  const [lastName, setLastName] = useBenefitDraftState("energy-bill", "lastName", billIdentity?.lastName ?? "")
  const [firstName, setFirstName] = useBenefitDraftState("energy-bill", "firstName", billIdentity?.firstName ?? "")
  const [middleName, setMiddleName] = useBenefitDraftState("energy-bill", "middleName", billIdentity?.middleName ?? "")
  // Keep the server's display value intact for non-Primary edits. A masked identifier is
  // unreadable, not an instruction to clear the stored value.
  const [ssn, setSsn] = useBenefitDraftState("energy-bill", "ssn", bill?.socialSecurityNumber ?? "")
  const [dateOfBirth, setDateOfBirth] = useBenefitDraftState("energy-bill", "dateOfBirth", billIdentity?.dateOfBirth?.slice(0, 10) ?? "")
  const [is18OrEmancipated, setIs18OrEmancipated] = useBenefitDraftState("energy-bill", "is18OrEmancipated", billIdentity?.is18OrEmancipated ?? false)
  const [isStillLivingAtResidence, setIsStillLivingAtResidence] = useBenefitDraftState("energy-bill", "isStillLivingAtResidence",
    billIdentity?.isStillLivingAtResidence ?? false,
  )
  const [isSpouse, setIsSpouse] = useBenefitDraftState("energy-bill", "isSpouse", billIdentity?.isSpouseOfApplicant ?? false)
  const [isDeceasedSpouse, setIsDeceasedSpouse] = useBenefitDraftState("energy-bill", "isDeceasedSpouse",
    billIdentity?.isDeceasedSpouseOfApplicant ?? false,
  )

  const canSave = canEdit && (appearance !== "0" || energyBillApplicantCurrent(identityContext, benefit))
  const resetForm = () => {
    const context = captureEnergyBillContext(benefit)
    setIdentityContext(context)
    const selectedAppearance = (benefit.energyBillInformation?.applicantAppearsAs ?? benefit.pseBenefitDetails?.applicantsAppearanceOnPseBill) == null
      ? "unanswered"
      : String(benefit.energyBillInformation?.applicantAppearsAs ?? benefit.pseBenefitDetails?.applicantsAppearanceOnPseBill)
    const currentBill = billIdentity
    const previewIdentity = selectedAppearance === "0" && context.applicant ? context.applicant : currentBill
    setAppearance(selectedAppearance)
    setLastName(previewIdentity?.lastName ?? "")
    setFirstName(previewIdentity?.firstName ?? "")
    setMiddleName(previewIdentity?.middleName ?? "")
    // Do not normalize a masked value on reset. Primary saves submit a blank transient value
    // and the server copies the canonical identifier inside its fenced transaction.
    setSsn(bill?.socialSecurityNumber ?? "")
    setDateOfBirth(previewIdentity?.dateOfBirth?.slice(0, 10) ?? "")
    setIs18OrEmancipated(currentBill?.is18OrEmancipated ?? false)
    setIsStillLivingAtResidence(currentBill?.isStillLivingAtResidence ?? false)
    setIsSpouse(currentBill?.isSpouseOfApplicant ?? false)
    setIsDeceasedSpouse(currentBill?.isDeceasedSpouseOfApplicant ?? false)
  }

  const save = async () => {
    if (!isEditing || isSaving || !canSave) return
    // Primary is an intent, not a client-side identity update. This also avoids validating or
    // round-tripping a masked SSN; the server re-reads the canonical applicant identity.
    const ssnDigits = appearance === "0" ? "" : normalizeEnergyBillSsn(ssn)
    if (ssnDigits === null) {
      toast.error("SSN must be empty or contain exactly 9 digits.")
      return
    }

    setIsSaving(true)
    try {
      const result = await saveBenefitChange(benefit.id, onUpdated, () => api.patch<BenefitDetailDto>(
        `/api/benefitapplications/${benefit.id}/energy-bill-information`,
        {
          concurrencyToken: editToken,
          applicantAppearsAs: appearance === "unanswered" ? null : Number(appearance),
          lastName,
          firstName,
          middleName,
          socialSecurityNumber: ssnDigits,
          dateOfBirth: dateOfBirth || null,
          copyApplicantDemographics: appearance === "0",
          // A restricted PSE projection omits the protected identifier. Tell the server to
          // preserve that existing value; a visible blank remains an explicit clear.
          preserveExistingSocialSecurityNumber: appearance !== "0" && ssnDigits === "" && identityContext.preserveUnreadableSsn,
          is18OrEmancipated,
          isStillLivingAtResidence,
          isSpouseOfApplicant: isSpouse,
          isDeceasedSpouseOfApplicant: isDeceasedSpouse,
        },
      ), "Energy bill information updated.")
      if (result.saved || result.reloaded) setIsEditing(false)
    } finally {
      setIsSaving(false)
    }
  }

  const heatingSources = programHeatingSourceDisplay(benefit, providerNames, resolvedEnergyServiceOptions)
  const renderHeatingSources = () => (
    <FieldGrid cols={2}>
      <Field label="PSE Energy Type" value={heatingSources.pseEnergyType} />
      <Field label="LIHEAP Primary Heating Service" value={heatingSources.liheapPrimaryHeatingService} />
      <Field label="LIHEAP Secondary Heating Service" value={heatingSources.liheapSecondaryHeatingService} />
      <Field label="SHEAP Heat Source" value={heatingSources.sheapHeatSource} />
    </FieldGrid>
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconBolt className="size-5" />
            <div>
              <CardTitle className="text-base">Energy Bill Information</CardTitle>
              <CardDescription>Shared bill identity and heating-source information</CardDescription>
            </div>
          </div>
          <EditableCardActions
            isEditing={isEditing}
            isSaving={isSaving}
            canEdit={canEdit}
            saveDisabled={!canSave}
            restorePanelFocus
            onEdit={() => {
              if (!canEdit) return
              resetForm()
              setIsEditing(true)
            }}
            onSave={save}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEditing ? (
          <div className="space-y-4">
            {appearance === "0" && !energyBillApplicantCurrent(identityContext, benefit) && <p role="status" className="text-sm text-destructive">The applicant changed or is unavailable. Cancel and reopen before copying applicant identity.</p>}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Applicant Appears As</label>
                <Select value={appearance} onValueChange={(value) => {
                  setAppearance(value)
                  if (value === "0") {
                    const applicant = identityContext.applicant
                    if (applicant) {
                      setLastName(applicant.lastName)
                      setFirstName(applicant.firstName)
                      setMiddleName(applicant.middleName)
                      setDateOfBirth(applicant.dateOfBirth)
                    }
                  }
                }} disabled={isSaving}>
                  <SelectTrigger aria-label="Applicant Appears As"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unanswered">Not answered</SelectItem>
                    <SelectItem value="0">Primary</SelectItem>
                    <SelectItem value="1">Co-customer</SelectItem>
                    <SelectItem value="2">Not Listed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {[
                ["Last Name", lastName, setLastName],
                ["First Name", firstName, setFirstName],
                ["Middle Name", middleName, setMiddleName],
              ].map(([label, value, setter]) => (
                <div key={label as string} className="space-y-1.5">
                  <label className="text-sm font-medium">{label as string}</label>
                  <Input
                    aria-label={label as string}
                    maxLength={50}
                    value={value as string}
                    disabled={isSaving || appearance === "0"}
                    readOnly={appearance === "0"}
                    onChange={(event) => (setter as (value: string) => void)(event.target.value)}
                  />
                </div>
              ))}
              {hasPseSection && <>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">SSN</label>
                  <Input
                    aria-label="SSN"
                    inputMode="numeric"
                    maxLength={11}
                    value={appearance === "0"
                      ? identityContext.applicant?.socialSecurityNumber ?? ""
                      : ssn}
                    disabled={isSaving || appearance === "0"}
                    readOnly={appearance === "0"}
                    onChange={(event) => setSsn(event.target.value)}
                  />
                </div>
              </>}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Date of Birth</label>
                  <Input
                    aria-label="Date of Birth"
                    type="date"
                    value={dateOfBirth}
                    disabled={isSaving || appearance === "0"}
                    readOnly={appearance === "0"}
                    onChange={(event) => setDateOfBirth(event.target.value)}
                  />
                </div>
            </div>
            {appearance === "0" && <p className="text-sm text-muted-foreground">The applicant’s name, date of birth and SSN will be copied when you save. Protected identifiers may not be displayed.</p>}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                ["18+ or Emancipated", is18OrEmancipated, setIs18OrEmancipated],
                ["Still Living at Residence", isStillLivingAtResidence, setIsStillLivingAtResidence],
                ["Spouse of Applicant", isSpouse, setIsSpouse],
                ["Deceased Spouse of Applicant", isDeceasedSpouse, setIsDeceasedSpouse],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={checked as boolean}
                    disabled={isSaving}
                    onCheckedChange={(value) =>
                      (setter as (checked: boolean) => void)(value === true)
                    }
                  />
                  {label as string}
                </label>
              ))}
            </div>
            <Separator />
            {renderHeatingSources()}
          </div>
        ) : (
          <>
            <FieldGrid>
              <Field
                label="Applicant Appears As"
                value={enumLabel(
                  pseAppearanceOnBillLabels,
                  sharedBill?.applicantAppearsAs ?? benefit.pseBenefitDetails?.applicantsAppearanceOnPseBill,
                )}
              />
              <Field
                label="Name on Bill"
                value={billIdentity ? formatPersonName(billIdentity) : "—"}
              />
              {hasPseSection && <Field label="SSN" value={fmtSsn(bill?.socialSecurityNumber)} />}
                <Field label="Date of Birth" value={fmtDate(billIdentity?.dateOfBirth)} />
                <Field label="18+ or Emancipated" value={boolLabel(billIdentity?.is18OrEmancipated)} />
                <Field label="Still Living at Residence" value={boolLabel(billIdentity?.isStillLivingAtResidence)} />
                <Field label="Spouse of Applicant" value={boolLabel(billIdentity?.isSpouseOfApplicant)} />
                <Field
                  label="Deceased Spouse of Applicant"
                  value={boolLabel(billIdentity?.isDeceasedSpouseOfApplicant)}
                />
            </FieldGrid>
            <Separator />
            {renderHeatingSources()}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function grantOperationKey() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`
}

type GrantTaskInput = {
  explanation?: string
  reasonCode?: number
  effectiveDate?: string
  replacementAmount?: number
  replacementRevision?: number
  replacementDate?: string
}

function grantActionLabel(target: string): string {
  if (target === "Proofed") return "Review grant"
  if (target === "Certified") return "Certify grant"
  if (target === "FixRequested" || target.toLowerCase().includes("correction")) return "Request corrections"
  return target.replace(/([a-z])([A-Z])/g, "$1 $2")
}

function grantReviewBadge(review: BenefitGrantReview | null, legacy: boolean) {
  const state = review?.state
  const tone = legacy || !review ? "neutral" : state === "Certified" ? "green" : state === "FixRequested" || state === "Invalidated" ? "amber" : "blue"
  const Icon = legacy || !review ? IconInfoCircle : state === "Certified" ? IconShieldCheck : state === "Proofed" ? IconCheck : tone === "amber" ? IconAlertTriangle : IconClock
  const label = legacy ? "Legacy review" : !review ? "Unavailable" : state === "Created" && grantCapability(review, "review") ? "Awaiting review" : state === "FixRequested" ? "Corrections requested" : state || "Unavailable"
  return <Badge variant={tone === "green" ? "success" : tone === "amber" ? "warning" : tone === "blue" ? "info" : "secondary"} className="grant-review-badge"><Icon className="size-3.5" />{label}</Badge>
}

function grantReviewDescription(review: BenefitGrantReview | null, legacy: boolean) {
  if (legacy) return "Legacy status and history are available in grant details."
  if (!review) return "Review authority unavailable. Reload before making changes."
  if (review.lastExplanation) return review.lastExplanation
  if (grantCapability(review, "review")) return "Review the grant, supporting information and vendor payments."
  if (grantCapability(review, "certify")) return "Ready for certification."
  if (review.state === "Certified") return "Review complete. Delivery is tracked separately."
  if (review.state === "FixRequested") return "Resolve the requested corrections before review resumes."
  if (review.state === "Invalidated") return "This grant has been invalidated. See history for the reason."
  return "Review actions depend on this file’s state and your role."
}

function grantDeliveryFacts(review: BenefitGrantReview | null) {
  const delivery = deliveryReadbackDisplay(review?.delivery)
  return { delivery, label: delivery.overallLabel }
}

function GrantIconButton({ label, ...props }: ComponentProps<typeof Button> & { label: string }) {
  return <TooltipProvider><Tooltip><TooltipTrigger asChild><Button {...props} variant="ghost" size="icon" className="size-8" aria-label={label} /></TooltipTrigger><TooltipContent>{label}</TooltipContent></Tooltip></TooltipProvider>
}

function useGrantTaskFields() {
  const [explanation, setExplanation] = useBenefitDraftState("grants", "task.explanation", "")
  const [reasonCode, setReasonCode] = useBenefitDraftState("grants", "task.reasonCode", "")
  const [effectiveDate, setEffectiveDate] = useBenefitDraftState("grants", "task.effectiveDate", "")
  const [replacementAmount, setReplacementAmount] = useBenefitDraftState("grants", "task.replacementAmount", "")
  const [replacementRevision, setReplacementRevision] = useBenefitDraftState("grants", "task.replacementRevision", "")
  const [replacementDate, setReplacementDate] = useBenefitDraftState("grants", "task.replacementDate", "")
  return { explanation, setExplanation, reasonCode, setReasonCode, effectiveDate, setEffectiveDate,
    replacementAmount, setReplacementAmount, replacementRevision, setReplacementRevision, replacementDate, setReplacementDate,
    reset: () => { setExplanation(""); setReasonCode(""); setEffectiveDate(""); setReplacementAmount(""); setReplacementRevision(""); setReplacementDate("") },
  }
}

function GrantTaskSheet({
  task,
  fields,
  benefit,
  workflow,
  vendorNames,
  selectableVendors,
  canEdit,
  saving,
  onClose,
  canAct,
  informationTab,
  onInformationTab,
  onRestoreFocus,
  onChooseTask,
  onOpenPayment,
  onApply,
  onRecover,
  onReplace,
}: {
  task: GrantTask
  fields: ReturnType<typeof useGrantTaskFields>
  benefit: BenefitDetailDto
  workflow: BenefitWorkflowResponse | null
  vendorNames: Record<string, string>
  selectableVendors: VendorPaymentVendorDto[]
  canEdit: boolean
  saving: boolean
  onClose: () => void
  canAct: boolean
  informationTab: GrantInformationTab
  onInformationTab: (tab: GrantInformationTab) => void
  onRestoreFocus: () => void
  onChooseTask: (name: GrantTaskName) => void
  onOpenPayment: (grant: BenefitGrantDto, payment?: ServiceProviderPaymentDto) => void
  onApply: (grant: BenefitGrantDto, target: string, input: GrantTaskInput) => void
  onRecover: (grant: BenefitGrantDto, input: GrantTaskInput) => void
  onReplace: (grant: BenefitGrantDto, input: GrantTaskInput) => void
}) {
  const { grant, review } = task
  const basis = task.basis ?? captureGrantTaskBasis(benefit, grant, workflow)
  const { explanation, setExplanation, reasonCode, setReasonCode, effectiveDate, setEffectiveDate,
    replacementAmount, setReplacementAmount, replacementRevision, setReplacementRevision, replacementDate, setReplacementDate } = fields
  const programName = getBenefitProgramLabel(grant.benefitProgramInstance.program)
  const capabilityKind: GrantCapabilityKind = task.task === "review" ? "review" : task.task === "certify" ? "certify" : task.task === "invalidate" ? "invalidate" : task.task === "certification-correction" ? "certification-correction" : "corrections"
  const reviewCapability = grantCapability(review, capabilityKind)
  const correctionCapability = grantCapability(review, "corrections")
  const reasons = basis.reasons
  const delivery = grantDeliveryFacts(review).delivery
  const vendorTotal = grant.serviceProviderPayments.reduce((sum, payment) => sum + payment.paymentAmount, 0)
  const source = `${programName} PY ${grant.benefitProgramInstance.year}`
  const requiresReason = task.task === "returned-recovery" ? false : task.task === "certification-correction" || reviewCapability?.requiresReason === true
  const selectedReason = validRequiredReason(reasonCode, reasons)
  const currentReason = reasons.find(reason => reason.code === selectedReason)
  const requiresExplanation = reviewCapability?.requiresExplanation === true || currentReason?.requiresExplanation === true || ["corrections", "invalidate", "certification-correction", "returned-recovery"].includes(task.task)
  const validCorrection = explanation.trim().length > 0 && (!requiresReason || selectedReason !== null)
  const parsedReplacementAmount = Number(replacementAmount)
  const parsedReplacementRevision = Number(replacementRevision)
  const validReplacement = replacementAmount.trim() !== "" && replacementRevision.trim() !== "" && validReplacementInput(parsedReplacementAmount, parsedReplacementRevision, replacementDate)
  const history = review?.history ?? []
  const actionButton = (label: string, onClick: () => void, disabled = false) => <Button onClick={onClick} disabled={disabled || saving || !canAct}>{label}</Button>
  const historyContent = <section aria-labelledby="grant-history-heading">
    <h3 id="grant-history-heading" className="grant-section-title">Grant review history</h3>
    {history.length === 0 ? <p className="grant-note">No grant review history is available here. Legacy status history is on the History / Admin tab.</p> : <ol className="grant-history-list">{history.map(item => <li key={item.eventId}>
      <p className="font-medium">{item.from} → {item.to}</p>
      <p className="grant-note">{fmtDateTime(item.occurredAt)}{item.actorId ? ` · ${item.actorId}` : ""}</p>
      {item.explanation && <p className="mt-1">{item.explanation}</p>}
    </li>)}</ol>}
  </section>

  return <SheetContent side="right" className="grant-task-sheet" aria-label={`${source} grant task`} onCloseAutoFocus={event => { event.preventDefault(); onRestoreFocus() }}>
    <SheetHeader className="grant-task-header">
      <p className="grant-eyebrow">{source}</p>
      <SheetTitle>{task.task === "payments" ? "Edit payments" : task.task === "certification-correction" ? "Certification correction" : task.task === "returned-recovery" ? "Recover returned review" : task.task === "replacement" ? "Author replacement generation" : task.task === "history" ? "Grant history" : task.task === "details" ? "Grant details" : task.task === "corrections" ? "Request corrections" : task.task === "invalidate" ? "Invalidate grant" : task.task === "certify" ? "Certify grant" : "Review grant"}</SheetTitle>
      <SheetDescription>{task.task === "review" || task.task === "certify" ? "Keep the award and vendor payments in view while making the decision." : "Review the selected grant. Nothing changes until you confirm an action."}</SheetDescription>
    </SheetHeader>
    <div className="grant-task-body" role="region" aria-label="Grant task details" tabIndex={0}>
      {task.editContext && !canAct && <p role="alert" className="mb-4 rounded-md border border-amber-400 p-3 text-sm">This grant or your access changed. Close this task and reload before submitting; the original draft has not been attached to a new version.</p>}
      <fieldset className="min-w-0" disabled={saving || (!!task.editContext && !canAct)}>
      <div className="grant-task-summary">
        <div><span>Grant amount</span><strong>{fmtMoney(grant.grantAmount)}</strong></div>
        <div><span>Vendor payments</span><strong>{fmtMoney(vendorTotal)}</strong></div>
        <div aria-label="Grant review status">{grantReviewBadge(review, workflow?.authority === "Legacy")}</div>
      </div>
      {(["review", "certify", "details", "history"].includes(task.task)) && <div className="space-y-4">
        {task.task === "certify" && <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm"><p className="font-medium">Confirm certification for this grant</p><p className="mt-1 text-muted-foreground">This records the selected grant’s certified evidence. It does not publish a pledge, create an export, or confirm bank settlement.</p></div>}
        <Tabs value={informationTab} onValueChange={value => { if (value === "award" || value === "evidence" || value === "history") onInformationTab(value) }} className="grant-task-tabs">
          <TabsList aria-label="Grant information"><TabsTrigger value="award">Award & vendors</TabsTrigger><TabsTrigger value="evidence">Supporting information</TabsTrigger><TabsTrigger value="history">History</TabsTrigger></TabsList>
          <TabsContent value="award">
            <section aria-labelledby="grant-award-heading">
              <h3 id="grant-award-heading" className="sr-only">Award and vendor payments</h3>
              <dl className="grant-evidence-list">
                <div><dt>Program year</dt><dd>{grant.benefitProgramInstance.year}</dd></div>
                <div><dt>Eligibility ceiling</dt><dd>{fmtMoney(basis.eligibility.length === 1 ? basis.eligibility[0]!.eligibleAmount : undefined)}</dd></div>
                <div><dt>Application date</dt><dd>{fmtDate(grant.applicationDate)}</dd></div>
                <div><dt>Certification date</dt><dd>{fmtDate(review?.effectiveCertificationDate ?? grant.certificationDate)}</dd></div>
                <div><dt>Outcomes</dt><dd>{grant.outcomes ? `${enumLabel(outcomeLabels, grant.outcomes.before)} → ${enumLabel(outcomeLabels, grant.outcomes.after)}` : "Not recorded"}</dd></div>
                <div><dt>Financial status</dt><dd><GrantStatusBadge status={grant.grantStatus} /></dd></div>
              </dl>
            </section>
            <VendorPaymentTable grant={grant} vendorNames={vendorNames} workflow={workflow} benefitId={benefit.id} delivery={delivery.milestones[1]!.label} canEdit={false} onOpenPayment={onOpenPayment} />
          </TabsContent>
          <TabsContent value="evidence">
            <section aria-labelledby="grant-evidence-heading">
              <h3 id="grant-evidence-heading" className="grant-section-title">Available supporting information</h3>
              <dl className="grant-evidence-list">
                <div><dt>Household records</dt><dd>{basis.householdRecords} record{basis.householdRecords === 1 ? "" : "s"} available</dd></div>
                <div><dt>Average monthly income</dt><dd>{fmtMoney(basis.monthlyAdjustedIncome)}</dd></div>
                <div><dt>Eligibility records</dt><dd>{basis.eligibilityRecords} determination{basis.eligibilityRecords === 1 ? "" : "s"}</dd></div>
                <div><dt>Calculation evidence</dt><dd>{basis.calculationAvailable ? "Saved calculation available" : "Not available"}</dd></div>
              </dl>
              <p className="grant-note">Available records do not establish that the file is complete or verified. Protected identifiers remain hidden here.</p>
            </section>
          </TabsContent>
          <TabsContent value="history">{historyContent}</TabsContent>
        </Tabs>
        {(task.task === "review" || task.task === "certify") && reviewCapability && <><p className="grant-callout"> {reviewCapability.explanation ?? `${grantActionLabel(reviewCapability.target)} is available for this grant.`}</p>{(requiresReason || requiresExplanation) && <div className="space-y-3 rounded-md border p-3">{requiresReason && <div className="space-y-2"><Label htmlFor="grant-review-reason">Reason code <span className="text-muted-foreground">Required</span></Label>{reasons.length > 0 ? <Select value={reasonCode} onValueChange={setReasonCode}><SelectTrigger id="grant-review-reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger><SelectContent>{reasons.map(reason => <SelectItem key={reason.code} value={String(reason.code)}>{reason.meaning}</SelectItem>)}</SelectContent></Select> : <><Input id="grant-review-reason" inputMode="numeric" value={reasonCode} onChange={event => setReasonCode(event.target.value)} /><p className="text-xs text-muted-foreground">Enter the approved positive reason code.</p></>}</div>}{requiresExplanation && <div className="space-y-2"><Label htmlFor="grant-review-explanation">Explanation</Label><Textarea id="grant-review-explanation" value={explanation} onChange={event => setExplanation(event.target.value)} /></div>}</div>}</>}
      </div>}
      {task.task === "payments" && <div className="space-y-4"><p className="text-sm text-muted-foreground">Vendor payments are separate from grant review. Payment-list inclusion does not confirm that money has transferred.</p><VendorPaymentTable grant={grant} vendorNames={vendorNames} workflow={workflow} benefitId={benefit.id} delivery={delivery.milestones[1]!.label} canEdit={canEdit} onOpenPayment={onOpenPayment} /><Button variant="outline" onClick={() => onOpenPayment(grant)} disabled={!canEdit || selectableVendors.length === 0 || (grant.benefitProgramInstance.program === 1 && grant.serviceProviderPayments.length >= 2)}><IconPlus className="size-4" />Add payment</Button>{grant.benefitProgramInstance.program === 1 && grant.serviceProviderPayments.length >= 2 && <p className="text-sm text-muted-foreground">LIHEAP allows no more than two vendor payments. Edit an existing payment instead.</p>}{!canEdit && <p className="text-sm text-muted-foreground">Your role or this file’s state cannot edit vendor payments.</p>}</div>}
      {(task.task === "corrections" || task.task === "invalidate") && <div className="space-y-4"><div className="rounded-md border border-amber-300/60 bg-amber-50/50 p-3 text-sm dark:bg-amber-950/20"><p className="font-medium">{task.task === "invalidate" ? "This records an audited invalidation" : "This may return the application and affected sibling reviews"}</p><p className="mt-1 text-muted-foreground">The server capability and confirmation below define the actual scope. Existing certified and payment records are not overwritten.</p></div><div className="space-y-2"><Label htmlFor="grant-correction-explanation">Explanation <span className="text-muted-foreground">Required</span></Label><Textarea id="grant-correction-explanation" value={explanation} onChange={event => setExplanation(event.target.value)} rows={5} /></div>{requiresReason && <div className="space-y-2"><Label htmlFor="grant-correction-reason">Reason code <span className="text-muted-foreground">Required</span></Label>{reasons.length > 0 ? <Select value={reasonCode} onValueChange={setReasonCode}><SelectTrigger id="grant-correction-reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger><SelectContent>{reasons.map(reason => <SelectItem key={reason.code} value={String(reason.code)}>{reason.meaning}</SelectItem>)}</SelectContent></Select> : <><Input id="grant-correction-reason" inputMode="numeric" value={reasonCode} onChange={event => setReasonCode(event.target.value)} /><p className="text-xs text-muted-foreground">Enter the approved positive reason code.</p></>}</div>}</div>}
      {task.task === "certification-correction" && <div className="space-y-4"><div className="rounded-md border p-3 text-sm"><p className="font-medium">Audited correction for {source}</p><p className="mt-1 text-muted-foreground">The certification source is derived from this selected grant. No internal program ID or review version is entered by staff.</p></div><div className="space-y-2"><Label htmlFor="certification-correction-reason">Reason code <span className="text-muted-foreground">Required</span></Label>{reasons.length > 0 ? <Select value={reasonCode} onValueChange={setReasonCode}><SelectTrigger id="certification-correction-reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger><SelectContent>{reasons.map(reason => <SelectItem key={reason.code} value={String(reason.code)}>{reason.meaning}</SelectItem>)}</SelectContent></Select> : <><Input id="certification-correction-reason" inputMode="numeric" value={reasonCode} onChange={event => setReasonCode(event.target.value)} /><p className="text-xs text-muted-foreground">Enter the approved positive reason code.</p></>}</div><div className="space-y-2"><Label htmlFor="certification-correction-explanation">Why is a correction needed? <span className="text-muted-foreground">Required</span></Label><Textarea id="certification-correction-explanation" value={explanation} onChange={event => setExplanation(event.target.value)} /></div><div className="space-y-2"><Label htmlFor="certification-correction-date">Effective certification date <span className="text-muted-foreground">Required</span></Label><Input id="certification-correction-date" type="date" value={effectiveDate} onChange={event => setEffectiveDate(event.target.value)} /></div></div>}
      {task.task === "returned-recovery" && <div className="space-y-4"><p className="text-sm text-muted-foreground">Recovery is available only because the server returned an explicit capability for this grant. The original return history remains intact.</p><div className="space-y-2"><Label htmlFor="returned-recovery-explanation">Recovery explanation <span className="text-muted-foreground">Required</span></Label><Textarea id="returned-recovery-explanation" value={explanation} onChange={event => setExplanation(event.target.value)} /></div>{requiresReason && reasons.length > 0 && <Select value={reasonCode} onValueChange={setReasonCode}><SelectTrigger aria-label="Recovery reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger><SelectContent>{reasons.map(reason => <SelectItem key={reason.code} value={String(reason.code)}>{reason.meaning}</SelectItem>)}</SelectContent></Select>}</div>}
      {task.task === "replacement" && <div className="space-y-4"><p className="text-sm text-muted-foreground">A fresh calculation, current revision, amount and application date are required. This authors a new immutable generation; it does not edit the predecessor.</p><div className="grid gap-3 sm:grid-cols-2"><div className="space-y-2"><Label htmlFor="replacement-amount">Replacement grant amount</Label><Input id="replacement-amount" inputMode="decimal" value={replacementAmount} onChange={event => setReplacementAmount(event.target.value)} /></div><div className="space-y-2"><Label htmlFor="replacement-revision">Current calculation revision</Label><Input id="replacement-revision" inputMode="numeric" value={replacementRevision} onChange={event => setReplacementRevision(event.target.value)} /></div><div className="space-y-2"><Label htmlFor="replacement-date">Application date</Label><Input id="replacement-date" type="date" value={replacementDate} onChange={event => setReplacementDate(event.target.value)} /></div></div></div>}
      </fieldset>
    </div>
    <SheetFooter className="grant-task-footer">
      <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
      {task.task === "review" && correctionCapability && <Button variant="secondary" onClick={() => onChooseTask("corrections")} disabled={saving || !canAct}>Request corrections</Button>}
      {task.task === "review" && reviewCapability && actionButton("Confirm proofing", () => onApply(grant, reviewCapability.target, { explanation: explanation.trim() || undefined, reasonCode: reviewCapability.requiresReason ? selectedReason ?? undefined : undefined }), (requiresExplanation && !explanation.trim()) || (requiresReason && selectedReason === null))}
      {task.task === "certify" && reviewCapability && actionButton("Confirm certification", () => onApply(grant, reviewCapability.target, { explanation: explanation.trim() || undefined, reasonCode: reviewCapability.requiresReason ? selectedReason ?? undefined : undefined }), (requiresExplanation && !explanation.trim()) || (requiresReason && selectedReason === null))}
      {(task.task === "corrections" || task.task === "invalidate") && reviewCapability && actionButton(task.task === "invalidate" ? "Invalidate grant" : "Request corrections", () => onApply(grant, reviewCapability.target, { explanation: explanation.trim(), reasonCode: reviewCapability.requiresReason ? selectedReason ?? undefined : undefined }), !validCorrection)}
      {task.task === "certification-correction" && reviewCapability && actionButton("Record correction", () => onApply(grant, "CertificationCorrection", { explanation: explanation.trim(), effectiveDate, reasonCode: selectedReason ?? undefined }), !explanation.trim() || !validGrantDate(effectiveDate) || selectedReason === null)}
      {task.task === "returned-recovery" && actionButton("Recover returned review", () => onRecover(grant, { explanation: explanation.trim() }), !explanation.trim())}
      {task.task === "replacement" && actionButton("Author replacement generation", () => onReplace(grant, { replacementAmount: parsedReplacementAmount, replacementRevision: parsedReplacementRevision, replacementDate }), !validReplacement)}
    </SheetFooter>
  </SheetContent>
}

function VendorPaymentTable({ grant, vendorNames, workflow, benefitId, delivery, canEdit, onOpenPayment, onRemovePayment }: { grant: BenefitGrantDto; vendorNames: Record<string, string>; workflow: BenefitWorkflowResponse | null; benefitId: string; delivery: string; canEdit: boolean; onOpenPayment: (grant: BenefitGrantDto, payment?: ServiceProviderPaymentDto) => void; onRemovePayment?: (grant: BenefitGrantDto, payment: ServiceProviderPaymentDto) => void }) {
  return <section className="grant-vendors" aria-label="Vendor payment details">
    <div className="grant-vendors-heading">
      <div><h3>Vendor payments</h3><p>Payment-list status is reported for the whole grant, not individual vendor payments.</p></div>
      <span className="grant-vendor-total">Total: {fmtMoney(grant.serviceProviderPayments.reduce((sum, payment) => sum + payment.paymentAmount, 0))}</span>
    </div>
    <Table className="grant-vendor-table" tabIndex={0} aria-label={`${getBenefitProgramLabel(grant.benefitProgramInstance.program)} vendor payments`}>
      <TableHeader><TableRow><TableHead>Vendor / service</TableHead><TableHead>Account</TableHead><TableHead className="text-right">Payment amount</TableHead><TableHead>Pledge</TableHead><TableHead>Payment list (grant)</TableHead>{canEdit && <TableHead><span className="sr-only">Actions</span></TableHead>}</TableRow></TableHeader>
      <TableBody>{grant.serviceProviderPayments.length === 0 ? <TableRow><TableCell colSpan={canEdit ? 6 : 5} className="grant-empty">No vendor payments.</TableCell></TableRow> : grant.serviceProviderPayments.map(payment => <TableRow key={payment.vendorId}>
        <TableCell className="font-medium">{vendorNames[String(payment.vendorId)] ?? "Vendor name unavailable"}</TableCell>
        <TableCell className="grant-account">{payment.accountNumber || "—"}</TableCell>
        <TableCell className="grant-money text-right">{fmtMoney(payment.paymentAmount)}</TableCell>
        <TableCell>{paymentPledgeLabel(workflow, benefitId, grant, payment.vendorId)}</TableCell>
        <TableCell className="grant-secondary">{delivery}</TableCell>
        {canEdit && <TableCell className="text-right"><ButtonGroup className="grant-record-actions ml-auto" aria-label={`${vendorNames[String(payment.vendorId)] ?? "Vendor"} payment actions`}>
          <GrantIconButton label={`Edit payment for ${vendorNames[String(payment.vendorId)] ?? "vendor"}`} onClick={() => onOpenPayment(grant, payment)}><IconEdit className="size-4" /></GrantIconButton>
          {onRemovePayment && <GrantIconButton label={`Remove payment for ${vendorNames[String(payment.vendorId)] ?? "vendor"}`} onClick={() => onRemovePayment(grant, payment)}><IconTrash className="size-4" /></GrantIconButton>}
        </ButtonGroup></TableCell>}
      </TableRow>)}</TableBody>
    </Table>
    {!canEdit && <p className="grant-vendor-lock"><IconLock className="size-3.5" />Vendor payments are read-only in this view. Use an authorized correction action to make changes.</p>}
  </section>
}

export function GrantsSection({
  benefit,
  vendorNames,
  selectablePrograms,
  selectableProgramsLoadFailed,
  selectableVendors,
  canEdit: requestedCanEdit,
  workflow = null,
  permissions = [],
  onUpdated,
  onReload = () => undefined,
}: {
  benefit: BenefitDetailDto
  vendorNames: Record<string, string>
  selectablePrograms: SelectableGrantProgramDto[]
  selectableProgramsLoadFailed: boolean
  selectableVendors: VendorPaymentVendorDto[]
  canEdit: boolean
  workflow?: BenefitWorkflowResponse | null
  permissions?: string[]
  onUpdated: (updated: BenefitDetailDto) => void
  onReload?: () => void
}) {
  const canEdit = requestedCanEdit && (workflow?.authority === "Legacy" ? workflow.display?.readUnavailable !== true : usableGrantAuthority(workflow))
  const reviewing = useBenefitReviewActive()
  const reviewingRef = useRef(reviewing); reviewingRef.current = reviewing
  const sectionRef = useRef<HTMLElement | null>(null)
  const removal = useGrantRemoval({ benefit, workflow, canEdit, onUpdated, section: sectionRef })
  const paymentOrigin = useRef<HTMLElement | null>(null)
  const paymentFocusKey = useRef<string | null>(null)
  const hasOpenEditors = useHasOpenBenefitEditors()
  const latestBenefit = useRef(benefit)
  latestBenefit.current = benefit
  const taskInFlight = useRef(false)
  const taskAttempt = useBenefitDraftRef("grants", "attempt", null)
  const menuOrigins = useRef(new Map<string, HTMLButtonElement>())
  const grantEditFile = useBenefitDraftRef("grants", "file", null)
  const grantOrigin = useRef<HTMLElement | null>(null)
  const grantFocusLabel = useRef("Add grant")
  const grantCompleted = useRef(false)
  const [dialogOpen, setDialogOpen, grantToken] = useBenefitEdit(benefit.concurrencyToken, "grant")
  const [selectedInstance, setSelectedInstance] = useBenefitDraftState("grants", "selectedInstance", "")
  const [editingGrant, setEditingGrant] = useBenefitDraftState("grants", "editingGrant", null)
  const [grantAmount, setGrantAmount] = useBenefitDraftState("grants", "grantAmount", "")
  const [outcomeBefore, setOutcomeBefore] = useBenefitDraftState("grants", "outcomeBefore", "")
  const [outcomeAfter, setOutcomeAfter] = useBenefitDraftState("grants", "outcomeAfter", "")
  const [applicationDate, setApplicationDate] = useBenefitDraftState("grants", "applicationDate",
    grantDefaults(benefit.applicationDate, 0).applicationDate,
  )
  const [isSaving, setIsSaving] = useBenefitSaving()
  const [paymentDialog, setPaymentDialog] = useBenefitDraftState("grants", "paymentDialog", null)
  const [vendorId, setVendorId] = useBenefitDraftState("grants", "vendorId", "")
  const [paymentAmount, setPaymentAmount] = useBenefitDraftState("grants", "paymentAmount", "")
  const [accountNumber, setAccountNumber] = useBenefitDraftState("grants", "accountNumber", "")
  const [secondVendorId, setSecondVendorId] = useBenefitDraftState("grants", "secondVendorId", "")
  const [secondAccountNumber, setSecondAccountNumber] = useBenefitDraftState("grants", "secondAccountNumber", "")
  const [secondPaymentAmount, setSecondPaymentAmount] = useBenefitDraftState("grants", "secondPaymentAmount", "")
  const [pseGasAccountNumber, setPseGasAccountNumber] = useBenefitDraftState("grants", "pseGasAccountNumber", "")
  const [pseElectricAccountNumber, setPseElectricAccountNumber] = useBenefitDraftState("grants", "pseElectricAccountNumber", "")
  const [expandedGrantKeys, setExpandedGrantKeys] = useState<string[]>([])
  const [editTask, setTask] = useBenefitDraftState("grants", "task", null)
  const [editInformationTab, setEditInformationTab] = useBenefitDraftState("grants", "task.informationTab", "award")
  const grantView = useGrantView()
  const [taskSaving, setTaskSaving] = useBenefitSaving()
  const taskOrigin = useRef<HTMLElement | null>(null)
  const taskCompleted = useRef(false)
  const taskFields = useGrantTaskFields()
  const focusGrantKey = useRef<string | null>(null)
  const addingLiheap = paymentDialog?.grant.benefitProgramInstance.program === 1 && !paymentDialog.payment
  const showSecondPayment = addingLiheap && paymentDialog.grant.serviceProviderPayments.length === 0
  const [taskEditing, setTaskEditing] = useBenefitEdit(benefit.concurrencyToken, "grant-task")
  const [paymentEditing, setPaymentEditing] = useBenefitEdit(benefit.concurrencyToken, "vendor-payment")
  const paymentEditingRef = useRef(paymentEditing); paymentEditingRef.current = paymentEditing
  const [splitEditing, setSplitEditing, splitToken] = useBenefitEdit(benefit.concurrencyToken, "payment-split")
  const splitContext = useBenefitDraftRef("grants", "splitContext", null)
  const reviewForGrant = (grant: BenefitGrantDto) => {
    if (!usableGrantAuthority(workflow)) return null
    const matches = workflow.grantReviews.filter(review => review.programId === grant.benefitProgramInstance.program && review.programYear === grant.benefitProgramInstance.year)
    return matches.length === 1 ? matches[0]! : null
  }

  const canEditGrant = (grant: BenefitGrantDto) => canEdit && (workflow?.authority === "Legacy" || actionableGrantReview(reviewForGrant(grant)))
  const view = grantView.view
  const selectedGrants = view ? benefit.benefitGrants.filter(grant => grant.benefitProgramInstance.program === view.programId && grant.benefitProgramInstance.year === view.programYear) : []
  const selectedGrant = selectedGrants.length === 1 ? selectedGrants[0]! : null
  const task: GrantTask | null = editTask ?? (view && selectedGrant ? {
    benefitId: benefit.id, grant: selectedGrant, review: reviewForGrant(selectedGrant), task: view.task, editContext: null, basis: null,
  } : null)
  if (task) focusGrantKey.current = grantInstanceKey(task.grant.benefitProgramInstance)
  const restoreTaskFocus = () => {
    if (!sectionRef.current?.isConnected || reviewingRef.current || paymentEditingRef.current) return
    const origin = taskOrigin.current
    const menu = focusGrantKey.current ? menuOrigins.current.get(focusGrantKey.current) : null
    const heading = document.getElementById("grants-workspace-heading") ?? document.querySelector<HTMLElement>("[data-benefit-editor-focus]")
    const target = taskCompleted.current ? heading : origin?.isConnected && origin.getClientRects().length ? origin
      : menu?.isConnected && menu.getClientRects().length ? menu : heading
    target?.focus()
  }

  const closeTask = (completed = false) => {
    if ((taskInFlight.current || isSaving || paymentEditing) && !completed) return
    taskCompleted.current = completed
    setTask(null)
    setTaskEditing(false)
    grantView.close()
    taskAttempt.current = null
  }

  const openTask = (grant: BenefitGrantDto, taskName: GrantTaskName, origin: HTMLElement | null = null) => {
    if (taskInFlight.current || isSaving || reviewing || hasOpenEditors) return
    const review = reviewForGrant(grant)
    const mutating = !["details", "history", "payments"].includes(taskName)
    const editContext = mutating && review ? captureGrantContext(benefit, grant, review) : null
    if (mutating && (!grantTaskAllowed(review, taskName) || !editContext || !currentGrantContext(editContext, benefit, workflow))) return
    taskCompleted.current = false
    taskOrigin.current = origin
    if (taskName === "details" || taskName === "history" || taskName === "payments") {
      grantView.select({ programId: grant.benefitProgramInstance.program, programYear: grant.benefitProgramInstance.year, task: taskName, tab: taskName === "history" ? "history" : "award" })
    } else {
      grantView.close()
      taskAttempt.current = null
      setEditInformationTab("award")
      taskFields.reset()
      setTask({ benefitId: benefit.id, grant, review, task: taskName, editContext, basis: captureGrantTaskBasis(benefit, grant, workflow) })
      // Capture the actual initial task and empty fields, not the preceding task's form.
      if (!setTaskEditing(true)) setTask(null)
    }
  }

  const primaryTask = (grant: BenefitGrantDto): GrantTaskName => {
    const review = reviewForGrant(grant)
    if (grantCapability(review, "review")) return "review"
    if (grantCapability(review, "certify")) return "certify"
    if (canEditGrant(grant)) return "payments"
    return "details"
  }

  const taskAlternatives = (grant: BenefitGrantDto): { name: GrantTaskName; label: string }[] => {
    const review = reviewForGrant(grant)
    const primary = primaryTask(grant)
    const alternatives: { name: GrantTaskName; label: string }[] = []
    const corrections = grantCapability(review, "corrections")
    if (corrections && primary !== "corrections") alternatives.push({ name: "corrections", label: "Request corrections" })
    if (grantCapability(review, "invalidate")) alternatives.push({ name: "invalidate", label: "Invalidate grant" })
    if (canEditGrant(grant) && primary !== "payments") alternatives.push({ name: "payments", label: "Edit payments" })
    if (grantTaskAllowed(review, "replacement")) alternatives.push({ name: "replacement", label: "Author replacement generation" })
    if (grantCapability(review, "certification-correction")) alternatives.push({ name: "certification-correction", label: "Correct certification" })
    if (grantTaskAllowed(review, "returned-recovery")) alternatives.push({ name: "returned-recovery", label: "Recover returned review" })
    return alternatives.filter((item, index, all) => all.findIndex(candidate => candidate.name === item.name) === index)
  }

  const currentTaskContext = () => taskEditing && !reviewing && task &&
    unchangedGrantTask(task.grant, task.review, task.basis, benefit, workflow)
    ? currentGrantContext(task.editContext, benefit, workflow) : null
  const activeContext = currentTaskContext()
  const canApplyTask = !!task && !!activeContext && grantTaskAllowed(activeContext.review, task.task)

  const executeTask = async (context: GrantEditContext, suffix: string, fields: Record<string, unknown>) => {
    if (taskInFlight.current) return
    const path = `/api/benefitapplications/${context.benefitId}/grants/${context.programId}/${context.programYear}/${suffix}`
    const body = { ...fields, concurrencyToken: context.concurrencyToken, reviewVersion: context.reviewVersion }
    const attempt = grantAttempt(taskAttempt.current, path, body, grantOperationKey)
    taskAttempt.current = attempt
    taskInFlight.current = true
    setTaskSaving(true)
    try {
      const updated = await api.post<BenefitDetailDto>(path, { ...body, idempotencyKey: attempt.key })
      if (latestBenefit.current.id === context.benefitId) { onUpdated(updated); onReload() }
      closeTask(true)
      requestAnimationFrame(restoreTaskFocus)
      toast.success("Grant action recorded.")
    } catch (error) {
      if (handledWorkspaceFailure(context.benefitId, error)) return
      if (error instanceof ApiError && error.status === 409 && latestBenefit.current.id === context.benefitId) onReload()
      toast.error(error instanceof Error ? error.message : "The grant action was rejected.")
    } finally { taskInFlight.current = false; setTaskSaving(false) }
  }

  const applyTask = async (_grant: BenefitGrantDto, target: string, input: GrantTaskInput) => {
    const current = currentTaskContext()
    if (!current) { toast.error("This grant changed while the editor was open. Reload before submitting."); return }
    const { context, review } = current
    const kind = task?.task as GrantCapabilityKind
    const capability = ["review", "certify", "corrections", "invalidate", "certification-correction"].includes(kind) ? grantCapability(review, kind) : undefined
    if (!capability || (target === "CertificationCorrection" ? kind !== "certification-correction" : capability.target !== target)) {
      toast.error("This grant-review action is not enabled by the server."); return
    }
    const reasons = workflow?.reasons.filter(reason => reason.group === "GrantReview") ?? []
    const reason = validRequiredReason(String(input.reasonCode ?? ""), reasons)
    const needsExplanation = capability.requiresExplanation || reasons.some(option => option.code === reason && option.requiresExplanation) || ["corrections", "invalidate", "certification-correction"].includes(kind)
    if ((capability.requiresReason || kind === "certification-correction") && reason === null) { toast.error("Enter a valid required reason code."); return }
    if (needsExplanation && !input.explanation?.trim()) { toast.error("Enter the required explanation."); return }
    if (kind === "certification-correction") {
      if (!validGrantDate(input.effectiveDate)) { toast.error("Enter a valid effective certification date."); return }
      await executeTask(context, "review/certification-correction", {
        reasonGroup: "GrantReview", reasonCode: reason, explanation: input.explanation!.trim(),
        certificationSourceProgramId: context.programId, certificationSourceProgramYear: context.programYear,
        effectiveCertificationDate: input.effectiveDate,
      })
    } else {
      await executeTask(context, "review", {
        state: target, ...(capability.requiresReason ? { reasonGroup: "GrantReview", reasonCode: reason } : {}),
        explanation: input.explanation?.trim() || undefined,
      })
    }
  }

  const recoverTask = async (_grant: BenefitGrantDto, input: GrantTaskInput) => {
    const current = currentTaskContext()
    if (!current || current.review.returnedRecovery?.enabled !== true || !input.explanation?.trim()) { toast.error("Returned recovery requires the unchanged current capability, token, and explanation."); return }
    const { context, review } = current
    if (task?.task !== "returned-recovery") return
    await executeTask(context, "review/returned-recovery", { state: "FixRequested", explanation: input.explanation.trim() })
  }

  const replaceTask = async (_grant: BenefitGrantDto, input: GrantTaskInput) => {
    const current = currentTaskContext()
    const review = current?.review
    if (!current || task?.task !== "replacement" || !grantTaskAllowed(review ?? null, "replacement") || !validReplacementInput(input.replacementAmount, input.replacementRevision, input.replacementDate)) { toast.error("An unchanged invalidated predecessor, fresh calculation, amount, revision, and application date are required for replacement."); return }
    await executeTask(current.context, "replacement", {
      predecessorGenerationId: current.context.predecessorGenerationId, revision: input.replacementRevision,
      applicationDate: input.replacementDate, grantAmount: input.replacementAmount,
    })
  }

  const eligibleOptions = availableGrantEligibility(
    benefit.benefitEligibility,
    benefit.benefitGrants,
    new Set(selectablePrograms.map((program) => program.id)),
  )

  const openGrantEditor = (grant: BenefitGrantDto | null) => {
    if (!canEdit || isSaving || reviewing || hasOpenEditors || (grant && !canEditGrant(grant))) return
    const context = captureGrantAuthoringContext(benefit, workflow, selectablePrograms, grant)
    if (!context) return
    // Capture before opening, so the baseline is the form the operator actually saw.
    grantEditFile.current = context
    setEditingGrant(grant)
    setSelectedInstance(grant ? grantInstanceKey(grant.benefitProgramInstance) : "")
    setGrantAmount(grant ? String(grant.grantAmount) : "")
    setOutcomeBefore(grant?.outcomes ? String(grant.outcomes.before) : "")
    setOutcomeAfter(grant?.outcomes ? String(grant.outcomes.after) : "")
    setApplicationDate((grant?.applicationDate ?? benefit.applicationDate).slice(0, 10))
    grantOrigin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    grantCompleted.current = false
    setDialogOpen(true)
  }
  const openDialog = () => openGrantEditor(null)
  const openCorrection = (grant: BenefitGrantDto) => openGrantEditor(grant)
  if (dialogOpen) grantFocusLabel.current = editingGrant
    ? `Edit ${getBenefitProgramLabel(editingGrant.benefitProgramInstance.program)} PY ${editingGrant.benefitProgramInstance.year} grant award` : "Add grant"
  const restoreGrantFocus = () => {
    if (!sectionRef.current?.isConnected || reviewingRef.current) return
    const origin = grantOrigin.current
    const opener = grantFocusLabel.current === "Add grant" ? sectionRef.current.querySelector<HTMLElement>("[data-add-grant]")
      : sectionRef.current.querySelector<HTMLElement>(`button[aria-label="${grantFocusLabel.current}"]`)
    const target = !grantCompleted.current && origin?.isConnected && origin.getClientRects().length ? origin
      : !grantCompleted.current && opener?.isConnected ? opener : document.getElementById("grants-workspace-heading")
    target?.focus()
  }
  const closeGrant = () => { if (!isSaving && !reviewing) setDialogOpen(false) }
  const dialogOptions = dialogOpen ? grantEditFile.current?.options ?? [] : eligibleOptions
  // This message is shown only when the matching eligibility determination says the existing
  // selectable grant is eligible. A selectable grant by itself may be protected or ineligible;
  // do not label those cases as an "eligible" duplicate.
  const existingEligibleGrant = !editingGrant && eligibleOptions.length === 0
    ? benefit.benefitGrants.find(grant =>
      selectablePrograms.some(program => program.id === grant.benefitProgramInstance.program) &&
      benefit.benefitEligibility.some(eligibility =>
        eligibility.isEligible &&
        eligibility.benefitProgramInstance.program === grant.benefitProgramInstance.program &&
        eligibility.benefitProgramInstance.year === grant.benefitProgramInstance.year))
    : undefined
  const selectedEligibility = dialogOptions.find((eligibility) => {
    const instance = eligibility.benefitProgramInstance
    return selectedInstance === grantInstanceKey(instance)
  })
  const outcomeOnlyCorrection = editingGrant !== null && editingGrant.serviceProviderPayments.length > 0

  const selectEligibility = (value: string) => {
    if (isSaving || reviewing || editingGrant) return
    setSelectedInstance(value)
    const selected = dialogOptions.filter(eligibility => value === grantInstanceKey(eligibility.benefitProgramInstance))
    setGrantAmount(selected.length === 1 ? String(selected[0]!.eligibleAmount) : "")
  }
  const canSaveGrant = dialogOpen && !reviewing && canEdit && (!editingGrant || canEditGrant(editingGrant)) &&
    grantAuthoringContextCurrent(grantEditFile.current, editingGrant, selectedInstance, benefit, workflow, selectablePrograms)

  const saveGrant = async () => {
    if (isSaving || !canSaveGrant) { toast.error("This editor is no longer current. Cancel and reopen before saving."); return }
    const amount = parseQuestionnaireAmount(grantAmount)
    if (!selectedEligibility || amount === null || amount <= 0 || !applicationDate) {
      toast.error("Select an eligible program and enter a valid amount and application date.")
      return
    }
    if ((outcomeBefore !== "" || outcomeAfter !== "") && (outcomeBefore === "" || outcomeAfter === "")) {
      toast.error("Choose both grant outcomes or leave both unanswered.")
      return
    }

    setIsSaving(true)
    try {
      const instance = selectedEligibility.benefitProgramInstance
      const fields = { concurrencyToken: grantToken, applicationDate, grantAmount: amount }
      const outcomes = outcomeBefore !== "" && outcomeAfter !== ""
        ? { outcomeBefore: Number(outcomeBefore), outcomeAfter: Number(outcomeAfter) }
        : {}
      const updated = editingGrant
        ? await api.patch<BenefitDetailDto>(`/api/benefitapplications/${benefit.id}/grants/${instance.program}/${instance.year}`, {
            ...fields,
            updateOutcomes: true,
            outcomeBefore: outcomeBefore === "" ? null : Number(outcomeBefore),
            outcomeAfter: outcomeAfter === "" ? null : Number(outcomeAfter),
          })
        : await api.post<BenefitDetailDto>(`/api/benefitapplications/${benefit.id}/grants`, {
            ...fields, ...outcomes, benefitProgramId: instance.program, programYear: instance.year, revision: instance.revision,
          })
      if (latestBenefit.current.id === benefit.id) { onUpdated(updated); onReload() }
      grantCompleted.current = true
      setDialogOpen(false)
      restoreGrantFocus()
      toast.success(editingGrant ? "Draft grant corrected." : "Grant added.")
    } catch (err) {
      if (handledWorkspaceFailure(benefit.id, err)) return
      const message = err instanceof Error ? err.message : "Failed to save grant."
      if (err instanceof ApiError && err.status === 409) {
        try {
          const latest = await api.get<BenefitDetailDto>(
            `/api/benefitapplications/${benefit.id}`,
          )
          onUpdated(latest)
          setDialogOpen(false)
        } catch {
          // Keep the original concurrency error when the refresh also fails.
        }
      }
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const grants = benefit.benefitGrants
  const paymentVendorOptions = vendorPaymentOptions(
    selectableVendors,
    [...grants.flatMap((grant) => grant.serviceProviderPayments.map((payment) => payment.vendorId)),
      ...(paymentDialog?.payment ? [paymentDialog.payment.vendorId] : [])],
    vendorNames,
  )

  if (paymentDialog) paymentFocusKey.current = grantInstanceKey(paymentDialog.grant.benefitProgramInstance)
  const focusPaymentsHeading = () => sectionRef.current?.querySelector<HTMLElement>("#grants-workspace-heading")?.focus()
  const restorePaymentFocus = () => {
    // Never move focus from an old unmounted completion or from the conflict/unknown owner.
    if (!sectionRef.current?.isConnected || reviewingRef.current || task || view) return
    const origin = paymentOrigin.current
    const menu = paymentFocusKey.current ? menuOrigins.current.get(paymentFocusKey.current) : null
    if (origin?.isConnected && origin.getClientRects().length && !origin.matches(":disabled")) origin.focus()
    else if (menu?.isConnected) menu.focus()
    else focusPaymentsHeading()
  }
  const closePaymentDialog = () => {
    if (isSaving || reviewing || !setPaymentEditing(false)) return
    setPaymentDialog(null)
  }

  const openPaymentDialog = (grant: BenefitGrantDto, payment?: ServiceProviderPaymentDto) => {
    const context = captureVendorPaymentContext(benefit, grant, workflow)
    if (!canEditGrant(grant) || !context || isSaving || reviewing || taskInFlight.current || hasOpenEditors) return
    if (!payment && grant.benefitProgramInstance.program === 1 && grant.serviceProviderPayments.length >= 2) {
      toast.error("LIHEAP allows no more than two vendor payments.")
      return
    }
    // Initialize before opening so the baseline contains the actual captured fields too.
    paymentOrigin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setPaymentDialog({ ...context, payment: payment ? structuredClone(payment) : undefined })
    setVendorId(payment ? String(payment.vendorId) : "")
    setPaymentAmount(payment ? String(payment.paymentAmount) : "")
    setAccountNumber(payment?.accountNumber ?? "")
    setSecondVendorId("")
    setSecondAccountNumber("")
    setSecondPaymentAmount("")
    setPaymentEditing(true)
  }
  const canSavePayment = !!paymentDialog && paymentEditing && !reviewing && canEditGrant(paymentDialog.grant) &&
    vendorPaymentContextCurrent(paymentDialog, benefit, workflow)

  // Display the captured allocation while editing, even if the current projection vanishes.
  // It is comparison material only; current saved prerequisites independently gate the send.
  const pseSplit = splitEditing ? splitContext.current?.split : benefit.psePaymentSplit
  const reducedPseSplit = pseSplit != null && pseSplit.grantAmount !== pseSplit.eligibleAmount
  const splitGrants = pseSplit ? grants.filter(grant => grant.benefitProgramInstance.program === pseSplit.programId && grant.benefitProgramInstance.year === pseSplit.programYear) : []
  const splitGrant = splitGrants.length === 1 ? splitGrants[0]! : null
  const canEditPseSplit = !!splitGrant && canEditGrant(splitGrant) && !!captureVendorPaymentContext(benefit, splitGrant, workflow)
  const splitCurrent = !splitEditing || (!!splitContext.current && vendorPaymentContextCurrent(splitContext.current.paymentContext, benefit, workflow) &&
    JSON.stringify(splitContext.current.split) === JSON.stringify(benefit.psePaymentSplit) && splitToken === benefit.concurrencyToken)
  const canApplyPseSplit = canEditPseSplit && splitCurrent && !reviewing && pseSplit?.isAvailable === true && pseSplit.canApply

  const setSplitAccount = (value: string, setter: (value: string) => void) => {
    if (isSaving || reviewing) return
    if (!splitEditing) {
      const context = splitGrant && captureVendorPaymentContext(benefit, splitGrant, workflow)
      if (!canApplyPseSplit || !context || !pseSplit || hasOpenEditors) return
      splitContext.current = { paymentContext: context, split: structuredClone(pseSplit) }
      if (!setSplitEditing(true)) return
    }
    setter(value)
  }
  const closeSplit = () => {
    if (isSaving || reviewing || !setSplitEditing(false)) return
    splitContext.current = null
    setPseGasAccountNumber("")
    setPseElectricAccountNumber("")
    focusPaymentsHeading()
  }

  const applySavedPseSplit = async () => {
    const split = pseSplit
    if (!split || !splitEditing || !canApplyPseSplit || isSaving) return
    const gasAccount = split.gasAmount > 0 ? pseGasAccountNumber.trim() : undefined
    const electricAccount = split.electricAmount > 0 ? pseElectricAccountNumber.trim() : undefined
    if ((split.gasAmount > 0 && !gasAccount) || (split.electricAmount > 0 && !electricAccount)) {
      toast.error("Enter an account number for every positive gas/electric allocation.")
      return
    }
    const allocationSummary = split.allocations.map((allocation) => `${allocation.fuel}: ${fmtMoney(allocation.amount)}`).join(", ")
    const confirmation = reducedPseSplit
      ? `Apply the reduced-grant allocation (${allocationSummary}) using the saved HELP cost basis? Existing vendor payments are never overwritten.`
      : `Apply the saved HELP split (${allocationSummary}) as vendor payments? Existing vendor payments are never overwritten.`
    if (!window.confirm(confirmation)) return

    setIsSaving(true)
    try {
      const updated = await api.post<BenefitDetailDto>(
        `/api/benefitapplications/${benefit.id}/grants/${split.programId}/${split.programYear}/vendor-payments/pse-split`,
        { concurrencyToken: splitToken, gasAccountNumber: gasAccount, electricAccountNumber: electricAccount },
      )
      if (latestBenefit.current.id === benefit.id) { onUpdated(updated); onReload() }
      setSplitEditing(false)
      splitContext.current = null
      setPseGasAccountNumber("")
      setPseElectricAccountNumber("")
      focusPaymentsHeading()
      toast.success("Saved HELP split applied as vendor payments.")
    } catch (err) {
      if (handledWorkspaceFailure(benefit.id, err)) return
      const message = err instanceof Error ? err.message : "Failed to apply the saved HELP split."
      if (err instanceof ApiError && err.status === 409) {
        try { onUpdated(await api.get<BenefitDetailDto>(`/api/benefitapplications/${benefit.id}`)) } catch { /* retain original error */ }
      }
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const savePayment = async () => {
    if (!paymentDialog || !canSavePayment || isSaving) return
    const amount = parseQuestionnaireAmount(paymentAmount)
    const selectedVendorId = Number(vendorId)
    const parsedSlots = addingLiheap ? parseLiheapVendorSlots([
      { vendorId, accountNumber, paymentAmount },
      ...(showSecondPayment ? [{ vendorId: secondVendorId, accountNumber: secondAccountNumber, paymentAmount: secondPaymentAmount }] : []),
    ]) : null
    if (parsedSlots?.error) { toast.error(parsedSlots.error); return }
    if (!addingLiheap && (!vendorId || amount === null || amount <= 0 || !Number.isInteger(selectedVendorId) || !accountNumber.trim())) {
      toast.error("Select a vendor and enter a positive amount and account number.")
      return
    }

    setIsSaving(true)
    try {
      const grant = paymentDialog.grant.benefitProgramInstance
      const basePath = `/api/benefitapplications/${benefit.id}/grants/${grant.program}/${grant.year}/vendor-payments`
      const updated = paymentDialog.payment
        ? await api.patch<BenefitDetailDto>(`${basePath}/${paymentDialog.payment.vendorId}`, {
            concurrencyToken: paymentDialog.concurrencyToken,
            paymentAmount: amount,
            accountNumber: accountNumber.trim(),
          })
        : parsedSlots?.payments
          ? await api.post<BenefitDetailDto>(`${basePath}/batch`, {
              concurrencyToken: paymentDialog.concurrencyToken, payments: parsedSlots.payments,
            })
          : await api.post<BenefitDetailDto>(basePath, {
              concurrencyToken: paymentDialog.concurrencyToken,
              vendorId: selectedVendorId,
              paymentAmount: amount,
              accountNumber: accountNumber.trim(),
            })
      if (latestBenefit.current.id === benefit.id) { onUpdated(updated); onReload() }
      paymentOrigin.current = null
      paymentFocusKey.current = null
      setPaymentDialog(null)
      setPaymentEditing(false)
      toast.success(paymentDialog.payment ? "Vendor payment updated." : "Vendor payment added.")
    } catch (err) {
      if (handledWorkspaceFailure(benefit.id, err)) return
      const message = err instanceof Error ? err.message : "Failed to save vendor payment."
      if (err instanceof ApiError && err.status === 409) {
        try {
          onUpdated(await api.get<BenefitDetailDto>(`/api/benefitapplications/${benefit.id}`))
          setPaymentDialog(null)
          setPaymentEditing(false)
        } catch {
          // Keep the original concurrency error when the refresh also fails.
        }
      }
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const splitSection = pseSplit && <Card className="grant-support-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{reducedPseSplit ? "PSE HELP reduced-grant allocation" : "PSE HELP payment split"}</CardTitle>
          <CardDescription>
            {reducedPseSplit
              ? `The authored grant is below the $${pseSplit.eligibleAmount.toFixed(2)} eligible amount. The server allocates the actual $${pseSplit.grantAmount.toFixed(2)} grant using the saved HELP gas/electric cost basis; these are not the original recommendations.`
              : `These amounts come from the saved server HELP calculation for PY ${pseSplit.programYear}; the browser does not recalculate usage.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {pseSplit.isAvailable ? <>
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <div><p className="text-muted-foreground">Eligible amount</p><p className="font-mono tabular-nums">{fmtMoney(pseSplit.eligibleAmount)}</p></div>
              <div><p className="text-muted-foreground">Authored grant</p><p className="font-mono tabular-nums">{fmtMoney(pseSplit.grantAmount)}</p></div>
              <div><p className="text-muted-foreground">{reducedPseSplit ? "Allocated gas" : "Gas"}</p><p className="font-mono tabular-nums">{fmtMoney(pseSplit.gasAmount)}</p></div>
              <div><p className="text-muted-foreground">{reducedPseSplit ? "Allocated electric" : "Electric"}</p><p className="font-mono tabular-nums">{fmtMoney(pseSplit.electricAmount)}</p></div>
            </div>
            {reducedPseSplit && <p className="text-xs text-muted-foreground">Original saved recommendation: Gas {fmtMoney(pseSplit.savedGasAmount)} / Electric {fmtMoney(pseSplit.savedElectricAmount)}. Cost basis: Gas {fmtMoney(pseSplit.gasCostBasis)} / Electric {fmtMoney(pseSplit.electricCostBasis)}.</p>}
            {pseSplit.unavailableReason && <p className="text-sm text-amber-700 dark:text-amber-300">{pseSplit.unavailableReason}</p>}
            {(splitEditing || canApplyPseSplit) && <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {pseSplit.gasAmount > 0 && <div className="space-y-1.5"><label className="text-sm font-medium" htmlFor="pse-split-gas-account">Gas account number</label><Input id="pse-split-gas-account" maxLength={50} value={pseGasAccountNumber} disabled={isSaving || reviewing} onChange={(event) => setSplitAccount(event.target.value, setPseGasAccountNumber)} /></div>}
              {pseSplit.electricAmount > 0 && <div className="space-y-1.5"><label className="text-sm font-medium" htmlFor="pse-split-electric-account">Electric account number</label><Input id="pse-split-electric-account" maxLength={50} value={pseElectricAccountNumber} disabled={isSaving || reviewing} onChange={(event) => setSplitAccount(event.target.value, setPseElectricAccountNumber)} /></div>}
            </div>}
            {(splitEditing || canApplyPseSplit) && <Button variant="outline" onClick={() => void applySavedPseSplit()} disabled={isSaving || !splitEditing || !canApplyPseSplit}>{reducedPseSplit ? "Review & apply reduced-grant allocation" : "Review & apply saved split"}</Button>}
            {splitEditing && !canApplyPseSplit && <p role="status">This allocation or its authority changed or is unavailable. Your original allocation and accounts are retained; cancel and reopen from the current saved read before applying.</p>}
            {!canEditPseSplit && <p className="text-sm text-muted-foreground">Your role or this workflow state cannot edit vendor payments.</p>}
          </> : <p className="text-sm text-amber-700 dark:text-amber-300">{pseSplit.unavailableReason ?? "A current saved HELP calculation is not available for allocation."}</p>}
          {splitEditing && <Button variant="ghost" onClick={closeSplit} disabled={isSaving || reviewing}>Cancel allocation edit</Button>}
        </CardContent>
      </Card>

  return (
    <section ref={sectionRef} className="grant-workspace" aria-labelledby="grants-workspace-heading">
      <header className="grant-workspace-heading">
        <div><p className="grant-eyebrow">Grants & vendor payments</p><h2 id="grants-workspace-heading" tabIndex={-1}>Grants & payments</h2><p>Manage each award, review its evidence and track where the money is going.</p></div>
        {canEdit && <Button data-add-grant size="sm" variant="outline" onClick={openDialog}><IconPlus className="size-4" />Add grant</Button>}
      </header>
      <div className="grant-ledger">
        <div className="grant-ledger-summary" aria-label="Grant totals">
          <strong>{grants.length} {grants.length === 1 ? "grant" : "grants"}</strong>
          <span>Awarded <strong>{fmtMoney(grants.reduce((sum, grant) => sum + grant.grantAmount, 0))}</strong></span>
          <span>Vendor payments <strong>{fmtMoney(grants.reduce((sum, grant) => sum + grant.serviceProviderPayments.reduce((total, payment) => total + payment.paymentAmount, 0), 0))}</strong></span>
          <span className="grant-settlement-note">Amounts are not bank settlement</span>
        </div>
        {grants.length === 0 ? <p className="grant-empty">No grants have been added.</p> : <div className="grant-ledger-scroll" role="region" aria-label="Grant ledger" tabIndex={0}>
            <Table className="grant-table" aria-label="Grants and payments">
              <TableHeader><TableRow><TableHead>Grant</TableHead><TableHead className="text-right">Award</TableHead><TableHead>Vendor payments</TableHead><TableHead>Review</TableHead><TableHead>Delivery</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>{grants.map((grant) => {
                const grantKey = grantInstanceKey(grant.benefitProgramInstance)
                const isExpanded = expandedGrantKeys.includes(grantKey)
                const review = reviewForGrant(grant)
                const primary = primaryTask(grant)
                const alternatives = taskAlternatives(grant)
                const delivery = grantDeliveryFacts(review).delivery
                const vendorTotal = grant.serviceProviderPayments.reduce((sum, payment) => sum + payment.paymentAmount, 0)
                const unassignedCents = Math.round((grant.grantAmount - vendorTotal) * 100)
                const taskLabel = primary === "payments" ? "Edit payments" : primary === "details" ? "View details" : primary === "certify" ? "Certify grant" : "Review grant"
                return <Fragment key={grantKey}>
                  <TableRow className="grant-row" data-expanded={isExpanded}>
                    <TableCell><div className="grant-identity"><Button variant="ghost" size="icon" className="size-7 shrink-0" aria-label={`${isExpanded ? "Hide" : "Show"} ${getBenefitProgramLabel(grant.benefitProgramInstance.program)} vendor payments`} aria-expanded={isExpanded} aria-controls={`vendor-payments-${grantKey}`} onClick={() => setExpandedGrantKeys(current => isExpanded ? current.filter(key => key !== grantKey) : [...current, grantKey])}>{isExpanded ? <IconChevronDown className="size-4" /> : <IconChevronRight className="size-4" />}</Button><div><p className="grant-name">{getBenefitProgramLabel(grant.benefitProgramInstance.program)}</p><p className="grant-subtext">Program year {grant.benefitProgramInstance.year}</p></div></div></TableCell>
                    <TableCell className="text-right"><p className="grant-money grant-award">{fmtMoney(grant.grantAmount)}</p><p className="grant-subtext">Eligible: {fmtMoney(benefit.benefitEligibility.find(item => item.benefitProgramInstance.program === grant.benefitProgramInstance.program && item.benefitProgramInstance.year === grant.benefitProgramInstance.year)?.eligibleAmount)}</p></TableCell>
                    <TableCell><p className="grant-money">{fmtMoney(vendorTotal)}</p><p className="grant-subtext">{grant.serviceProviderPayments.length} payment{grant.serviceProviderPayments.length === 1 ? "" : "s"}{unassignedCents > 0 ? ` · ${fmtMoney(unassignedCents / 100)} unassigned` : unassignedCents === 0 ? " · fully assigned" : ` · ${fmtMoney(-unassignedCents / 100)} over award`}</p></TableCell>
                    <TableCell>{grantReviewBadge(review, workflow?.authority === "Legacy")}<p className="grant-review-description">{grantReviewDescription(review, workflow?.authority === "Legacy")}</p></TableCell>
                    <TableCell><dl className="grant-delivery"><div><dt>Export</dt><dd>{delivery.milestones[0]?.label ?? "Unavailable"}</dd></div><div><dt>Payment list</dt><dd>{delivery.milestones[1]?.label ?? "Unavailable"}</dd></div></dl>{delivery.unavailable && <p className="grant-subtext">Reconciliation needed</p>}</TableCell>
                    <TableCell><div className="space-y-1"><ButtonGroup className="grant-row-actions" data-emphasis={primary === "details" ? "secondary" : "primary"} aria-label={`${getBenefitProgramLabel(grant.benefitProgramInstance.program)} actions`}><Button size="sm" className="min-w-0 flex-1" variant={primary === "details" ? "outline" : "default"} onClick={event => openTask(grant, primary, event.currentTarget)}>{taskLabel}</Button><ButtonGroupSeparator /><DropdownMenu><DropdownMenuTrigger asChild><Button ref={element => { if (element) menuOrigins.current.set(grantKey, element); else menuOrigins.current.delete(grantKey) }} size="sm" variant={primary === "details" ? "outline" : "default"} className="w-9 px-0" aria-label={`More actions for ${getBenefitProgramLabel(grant.benefitProgramInstance.program)} PY ${grant.benefitProgramInstance.year}`}><IconChevronDown className="size-4" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-56"><DropdownMenuItem disabled>{getBenefitProgramLabel(grant.benefitProgramInstance.program)} · PY {grant.benefitProgramInstance.year}</DropdownMenuItem>{alternatives.map(item => <DropdownMenuItem key={item.name} onSelect={() => requestAnimationFrame(() => openTask(grant, item.name, menuOrigins.current.get(grantKey) ?? null))}>{item.label}</DropdownMenuItem>)}{canEditGrant(grant) && grant.serviceProviderPayments.length === 0 && !grant.certificationDate && <DropdownMenuItem onSelect={() => requestAnimationFrame(() => removal.open(grant, undefined, menuOrigins.current.get(grantKey) ?? null))}>Remove grant</DropdownMenuItem>}</DropdownMenuContent></DropdownMenu></ButtonGroup><ButtonGroup className="grant-record-actions" aria-label={`${getBenefitProgramLabel(grant.benefitProgramInstance.program)} PY ${grant.benefitProgramInstance.year} record actions`}>{primary !== "details" && <GrantIconButton label={`View ${getBenefitProgramLabel(grant.benefitProgramInstance.program)} PY ${grant.benefitProgramInstance.year} grant`} onClick={event => openTask(grant, "details", event.currentTarget)}><IconEye className="size-4" /></GrantIconButton>}{canEditGrant(grant) && canCorrectDraftGrant(grant) && <GrantIconButton label={`Edit ${getBenefitProgramLabel(grant.benefitProgramInstance.program)} PY ${grant.benefitProgramInstance.year} grant award`} onClick={() => openCorrection(grant)}><IconEdit className="size-4" /></GrantIconButton>}<GrantIconButton label={`History for ${getBenefitProgramLabel(grant.benefitProgramInstance.program)} PY ${grant.benefitProgramInstance.year} grant`} onClick={event => openTask(grant, "history", event.currentTarget)}><IconHistory className="size-4" /></GrantIconButton></ButtonGroup></div></TableCell>
                  </TableRow>
                  {isExpanded && <TableRow id={`vendor-payments-${grantKey}`} className="grant-vendor-expansion"><TableCell colSpan={6} className="p-0"><VendorPaymentTable grant={grant} vendorNames={vendorNames} workflow={workflow} benefitId={benefit.id} delivery={delivery.milestones[1]!.label} canEdit={canEditGrant(grant)} onOpenPayment={openPaymentDialog} onRemovePayment={removal.open} /></TableCell></TableRow>}
                </Fragment>
              })}</TableBody>
            </Table>
          </div>}
      </div>
      <p className="grant-workspace-footnote"><IconInfoCircle className="size-4" />Review and certification apply to the grant. Pledges apply to vendor payments; export and payment-list status are tracked separately. Payment-list inclusion does not confirm that money has transferred.</p>
      <div className="grant-next-context"><IconShieldCheck className="size-5" /><div><h3>Decisions stay with the award</h3><p>Review and certify here. Use the processing workspaces for batch exports and vendor payment lists.</p><div className="grant-processing-links">{permissions.includes("benefit-runs:execute") && <Link to="/ascii-exports">Open exports</Link>}{permissions.includes("benefit-runs:view") && <Link to="/benefit-runs">Open payment lists</Link>}</div></div></div>
      {splitSection}
      {removal.dialog}

      {!paymentEditing && !dialogOpen && !removal.editing && !reviewing && <Sheet open={task !== null || view !== null} onOpenChange={open => !open && closeTask()}>
        {task ? <GrantTaskSheet key={`${grantInstanceKey(task.grant.benefitProgramInstance)}-${task.task}`} task={task} fields={taskFields} benefit={benefit} workflow={workflow} vendorNames={vendorNames} selectableVendors={selectableVendors} canEdit={canEditGrant(task.grant) && benefit.id === task.benefitId} saving={taskSaving || isSaving} canAct={canApplyTask} informationTab={editTask ? editInformationTab : view!.tab} onInformationTab={editTask ? setEditInformationTab : grantView.changeTab} onRestoreFocus={restoreTaskFocus} onChooseTask={name => { if (canApplyTask && !taskInFlight.current && grantTaskAllowed(activeContext!.review, name)) setTask({ ...task, task: name }) }} onClose={() => closeTask()} onOpenPayment={openPaymentDialog} onApply={(grant, target, input) => void applyTask(grant, target, input)} onRecover={(grant, input) => void recoverTask(grant, input)} onReplace={(grant, input) => void replaceTask(grant, input)} /> : view && <SheetContent onCloseAutoFocus={event => { event.preventDefault(); restoreTaskFocus() }}>
          <SheetHeader><SheetTitle>Selected grant unavailable</SheetTitle><SheetDescription>The selected program/year no longer identifies one saved grant. No previous grant data or authority has been retained for this view.</SheetDescription></SheetHeader>
          <SheetFooter><Button variant="outline" onClick={() => closeTask()}>Close grant view</Button></SheetFooter>
        </SheetContent>}
      </Sheet>}

      <Dialog open={dialogOpen && !reviewing} onOpenChange={(open) => !open && closeGrant()}>
        <DialogContent className="grant-editor-dialog" onCloseAutoFocus={event => { event.preventDefault(); restoreGrantFocus() }}>
          <DialogHeader>
            <DialogTitle>{editingGrant ? "Correct Draft Grant" : "Add Grant"}</DialogTitle>
            <DialogDescription>
              {editingGrant
                ? outcomeOnlyCorrection
                  ? "Correct the whole-program outcomes only. Vendor payments, amount, date and financial status remain unchanged."
                  : "Correct amount, date or the whole-program outcomes against current eligibility. Financial status and payment records are not changed."
                : "Select an eligible program. Amount and date start with values already known on the application."}
            </DialogDescription>
          </DialogHeader>
          {dialogOptions.length === 0 ? (
            <div className="space-y-2 text-sm text-muted-foreground">
              {editingGrant ? (
                <p>Calculate current eligibility before correcting this grant.</p>
              ) : selectableProgramsLoadFailed ? (
                <p>Selectable programs could not be loaded. Reload the page and try again.</p>
              ) : existingEligibleGrant ? (
                <p>
                  {getBenefitProgramLabel(existingEligibleGrant.benefitProgramInstance.program)} PY {existingEligibleGrant.benefitProgramInstance.year} already has an eligible grant on this application. Adding another grant would duplicate the award. Use <strong>Add payment</strong> for a new vendor payment; use <strong>Edit payment</strong> to change an existing vendor payment; use <strong>Correct Draft Grant</strong> for award changes when that action is available.
                </p>
              ) : (
                <p>No eligible selectable programs remain for this application.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Benefit Program</label>
                <Select value={selectedInstance} onValueChange={selectEligibility} disabled={isSaving || editingGrant !== null}>
                  <SelectTrigger aria-label="Benefit Program"><SelectValue placeholder="Select a program" /></SelectTrigger>
                  <SelectContent>
                    {dialogOptions.map((eligibility, index) => {
                      const instance = eligibility.benefitProgramInstance
                      const program = selectablePrograms.find((option) => option.id === instance.program)
                      const value = grantInstanceKey(instance)
                      return (
                        <SelectItem key={`${value}-${index}`} value={value}>
                          {program?.name ?? getBenefitProgramLabel(instance.program)} — {instance.year}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Grant Amount</label>
                  <Input
                    aria-label="Grant Amount"
                    type="text"
                    inputMode="decimal"
                    value={grantAmount}
                    disabled={isSaving || outcomeOnlyCorrection}
                    onChange={(event) => setGrantAmount(event.target.value)}
                  />
                  {selectedEligibility && (
                    <p className="text-xs text-muted-foreground">
                      Maximum eligible: {fmtMoney(selectedEligibility.eligibleAmount)}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Application Date</label>
                  <Input
                    aria-label="Application Date"
                    type="date"
                    value={applicationDate}
                    disabled={isSaving || outcomeOnlyCorrection}
                    onChange={(event) => setApplicationDate(event.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-3 rounded-md border p-3">
                  <div>
                    <p className="text-sm font-medium">Program grant outcomes (optional)</p>
                    <p className="text-xs text-muted-foreground">{editingGrant ? outcomeOnlyCorrection ? "This grant has vendor payments, so this save is limited to the whole-program Before/After pair. Vendor payments do not have independent outcomes." : "Correct the existing whole-program Before/After pair. Vendor payments do not have independent outcomes." : "Record one Before/After pair for this program grant, not for individual vendor payments. Leave both fields unrecorded if the outcomes are not yet known."}</p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Outcome Before</label>
                      <Select value={outcomeBefore} onValueChange={(value) => setOutcomeBefore(value === "__clear__" ? "" : value)} disabled={isSaving}>
                        <SelectTrigger aria-label="Outcome Before"><SelectValue placeholder="Not recorded" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__clear__">Not recorded</SelectItem>
                          {Object.entries(outcomeLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Outcome After</label>
                      <Select value={outcomeAfter} onValueChange={(value) => setOutcomeAfter(value === "__clear__" ? "" : value)} disabled={isSaving}>
                        <SelectTrigger aria-label="Outcome After"><SelectValue placeholder="Not recorded" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__clear__">Not recorded</SelectItem>
                          {Object.entries(outcomeLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
            </div>
          )}
          {selectedInstance && !canSaveGrant && !isSaving && <p role="status" className="text-sm text-amber-700 dark:text-amber-300">This grant, eligibility or its authority changed or is unavailable. Your original draft is retained; cancel and reopen from the current saved read before saving.</p>}
          <DialogFooter>
            <Button variant="outline" onClick={closeGrant} disabled={isSaving || reviewing}>
              Cancel
            </Button>
            <Button onClick={saveGrant} disabled={isSaving || !canSaveGrant}>
              {isSaving && <IconLoader2 className="size-4 animate-spin" />}
              {editingGrant ? "Save Correction" : "Add Grant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={paymentEditing && paymentDialog !== null && !reviewing} onOpenChange={(open) => !open && closePaymentDialog()}>
        <DialogContent className="grant-editor-dialog sm:max-w-2xl" onCloseAutoFocus={event => { event.preventDefault(); restorePaymentFocus() }}>
          <DialogHeader>
            <DialogTitle>{paymentDialog?.payment ? "Edit Vendor Payment" : "Add Vendor Payment"}</DialogTitle>
            <DialogDescription>
              {paymentDialog?.payment
                ? "Update this payment without changing its contract allocations or other vendor payments."
                : addingLiheap
                  ? "LIHEAP allows up to two vendors. Leave an unused slot completely blank. Entered payments save together. Outcomes remain on the whole program grant."
                  : "Add one payment to this grant. Other payments on the grant remain unchanged."}
            </DialogDescription>
          </DialogHeader>
          {!canSavePayment && !isSaving && <p role="status">This grant or its authority changed or is unavailable. Your original payment draft is retained; cancel and reopen from the current saved read before saving.</p>}
          {addingLiheap && <p className="text-sm font-medium">Vendor payment 1</p>}
          <VendorPaymentFields
            vendorId={vendorId}
            vendors={paymentDialog?.payment ? paymentVendorOptions : selectableVendors}
            vendorDisabled={!!paymentDialog?.payment}
            accountNumber={accountNumber}
            paymentAmount={paymentAmount}
            disabled={isSaving}
            onVendorChange={setVendorId}
            onAccountNumberChange={setAccountNumber}
            onPaymentAmountChange={setPaymentAmount}
          />
          {showSecondPayment && <div className="space-y-3 border-t pt-3">
            <p className="text-sm font-medium">Vendor payment 2 (optional)</p>
            <VendorPaymentFields idPrefix="vendor-payment-2" vendorId={secondVendorId} vendors={selectableVendors}
              vendorDisabled={false} accountNumber={secondAccountNumber} paymentAmount={secondPaymentAmount}
              disabled={isSaving} onVendorChange={setSecondVendorId} onAccountNumberChange={setSecondAccountNumber}
              onPaymentAmountChange={setSecondPaymentAmount} />
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={closePaymentDialog} disabled={isSaving}>Cancel</Button>
            <Button onClick={savePayment} disabled={isSaving || !canSavePayment || (paymentDialog?.payment ? paymentVendorOptions : selectableVendors).length === 0}>
              {isSaving && <IconLoader2 className="size-4 animate-spin" />}
              {paymentDialog?.payment ? "Save Payment" : addingLiheap ? "Save Payments" : "Add Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

function EligibilitySection({ eligibility }: { eligibility: BenefitEligibilityDto[] }) {
  if (eligibility.length === 0) return null
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Eligibility Determinations</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Program</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Eligible</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eligibility.map((e, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Badge variant="outline">
                      {getBenefitProgramLabel(e.benefitProgramInstance.program)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{e.benefitProgramInstance.year}</TableCell>
                  <TableCell>
                    <Badge variant={e.isEligible ? "default" : "destructive"}>
                      {e.isEligible ? "Eligible" : "Not Eligible"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs tabular-nums">{fmtMoney(e.eligibleAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

function PledgeInstructionHistorySection({
  benefit,
  generations,
  canEdit,
}: {
  benefit: BenefitDetailDto
  generations: BenefitPledgeGeneration[]
  canEdit: boolean
}) {
  const rows = generations
  const [saving, setSaving] = useBenefitSaving()
  const inFlight = useRef(false)
  const hasOpenEditor = useHasOpenBenefitEditors()
  const current = rows.filter((row) => row.isCurrent)

  if (generations.length === 0) return null

  const updateFlags = async (row: BenefitPledgeGeneration, field: "urgent" | "calledIn" | "updated", value: boolean) => {
    if (inFlight.current) return
    if (hasOpenEditor) { toast.error("Save or cancel the open editor before changing pledge flags."); return }
    inFlight.current = true
    const next = { urgent: row.urgent, calledIn: row.calledIn, updated: row.updated, [field]: value }
    setSaving(true)
    try {
      await api.patch<BenefitPledgeGeneration[]>(
        `/api/pledges/applications/${benefit.id}/grants/${row.programId}/${row.programYear}/vendors/${row.vendorId}/flags`,
        { ...next, flagsVersion: row.flagsVersion, concurrencyToken: benefit.concurrencyToken },
      )
      toast.success("Vendor payment flags saved.")
    } catch (error) {
      if (handledWorkspaceFailure(benefit.id, error)) return
      toast.error(error instanceof Error ? error.message : "The flags could not be saved. Reload and retry.")
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <IconHistory className="size-5" />
          <CardTitle className="text-base">Pledge generation history</CardTitle>
          <Badge variant="outline">{generations.length}</Badge>
        </div>
        <CardDescription>Immutable amount/account generations are shown with the current vendor flags. A flag edit never changes financial ownership.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table aria-label="Pledge generation history" tabIndex={0}>
            <TableHeader><TableRow>
              <TableHead>Program / vendor</TableHead><TableHead>Status</TableHead><TableHead>Amount</TableHead><TableHead>Created</TableHead>
              <TableHead>Urgent</TableHead><TableHead>Called-in</TableHead><TableHead>Updated</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((row) => {
                const disabled = !canEdit || !row.isCurrent || saving || hasOpenEditor
                return <TableRow key={row.generationId}>
                  <TableCell className="whitespace-nowrap">{getBenefitProgramLabel(row.programId)} {row.programYear} / vendor {row.vendorId}</TableCell>
                  <TableCell><Badge variant={row.isCurrent ? "secondary" : "outline"}>{row.status}</Badge>{row.sourceGenerationId && <div className="text-xs text-muted-foreground">refresh of prior generation</div>}</TableCell>
                  <TableCell>{fmtMoney(row.amount)}</TableCell>
                  <TableCell className="text-xs">{new Date(row.createdAt).toLocaleString()}</TableCell>
                  {["urgent", "calledIn", "updated"].map((field) => <TableCell key={field}>
                    <Checkbox aria-label={`${field} for vendor ${row.vendorId}`} checked={row[field as keyof BenefitPledgeGeneration] as boolean} disabled={disabled}
                      onCheckedChange={(checked) => { if (checked !== "indeterminate") void updateFlags(row, field as "urgent" | "calledIn" | "updated", checked) }} />
                  </TableCell>)}
                </TableRow>
              })}
            </TableBody>
          </Table>
        </div>
        {current.some((row) => row.flagsVersion > 0) && <p className="px-4 py-3 text-xs text-muted-foreground">Flags use an independent version fence. If another staff member saves first, reload rather than overwrite their edit.</p>}
      </CardContent>
    </Card>
  )
}

function LiheapDetailsSection({
  details,
  providerNames,
  twoYearCertState,
  receivedEapLastYear,
}: {
  details: LiheapBenefitDto
  providerNames: Record<string, string>
  twoYearCertState: number
  receivedEapLastYear: boolean
}) {
  const fmtProvidedService = (svc: ProvidedServiceDto | null) => {
    if (!svc) return "—"
    const serviceLabel = formatServiceLabel(svc.service)
    const providerName = providerNames[String(svc.provider)] ?? `Provider ${svc.provider}`
    return `${serviceLabel} (${providerName})`
  }
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <IconFlame className="size-5" />
          <CardTitle className="text-base">LIHEAP Details</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldGrid>
          <Field label="Primary Heating Service" value={fmtProvidedService(details.primaryHeatingService)} />
          <Field label="Secondary Heating Service" value={fmtProvidedService(details.secondaryHeatingService)} />
          <Field label="Number of Bedrooms" value={fmt(details.numberOfBedrooms)} />
          <Field label="Heat with Rent" value={boolLabel(details.heatWithRent)} />
          <Field label="OES" value={boolLabel(details.oes)} />
          <Field label="OES Total" value={fmtMoney(details.oesTotal)} />
          <Field label="Previous Assistance" value={boolLabel(details.previousAssistance)} />
          <Field label="Previous Assistance Amount" value={fmtMoney(details.previousAssistanceAmount)} />
          <Field label="Received Energy Assistance" value={boolLabel(details.receivedEnergyAssistanceProgram)} />
          <Field label="Conservation Education" value={boolLabel(details.conservationEducation)} />
          <Field label="Life-Threatening Energy Crisis" value={boolLabel(details.isLifeThreateningEnergyCrisis)} />
        </FieldGrid>
        <Separator />
        <FieldGrid>
          <Field label="Total Annual Electric Costs" value={fmtMoney(details.totalAnnualElectricCosts)} />
          <Field label="Rent Amount Past Due" value={fmtMoney(details.rentAmountPastDue)} />
          <Field label="Water Amount Past Due" value={fmtMoney(details.waterAmountPastDue)} />
          <Field label="Cooling Cost" value={fmtMoney(details.coolingCost)} />
        </FieldGrid>
        <Separator />
        <FieldGrid>
          <Field label="Used Backup" value={boolLabel(details.usedBackup)} />
          <Field label="Used Surrogate" value={boolLabel(details.usedSurrogate)} />
          <Field label="Received EEAP" value={boolLabel(details.receivedEmergencyEnergyAssistanceProgram)} />
          <Field label="Received EAP Last Year" value={boolLabel(receivedEapLastYear)} />
          <Field label="2-Year Recert Opt-in Status" value={enumLabel(twoYearCertStateLabels, twoYearCertState)} />
        </FieldGrid>
      </CardContent>
    </Card>
  )
}

function PseDetailsSection({
  benefitId,
  applicationDate,
  programYear,
  details,
  twoYearCertState,
}: {
  benefitId: string
  applicationDate: string
  programYear: number | null | undefined
  details: PseBenefitDto
  twoYearCertState: number
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <IconBolt className="size-5" />
          <CardTitle className="text-base">PSE HELP Details</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldGrid>
          <Field label="PSE Application Type" value={enumLabel(pseApplicationTypeLabels, details.pseApplicationType)} />
          <Field label="Energy Type" value={details.energyType != null ? enumLabel(pseEnergyTypeLabels, details.energyType) : "—"} />
          <Field label="Date Moved In" value={fmtDate(details.dateMovedInResidence)} />
          <Field label="Monthly Income" value={fmtMoney(details.monthlyIncome)} />
          <Field label="LIHEAP Cost" value={fmtMoney(details.liheapCost)} />
          <Field label="Received EAP This Year" value={boolLabel(details.recvdEAPThisYear)} />
          <Field label="AMI Opt-Out" value={boolLabel(details.advancedMeteringInfrastructureOptOut)} />
        </FieldGrid>
        <Separator />
        <PseBackupCostDetails
          benefitId={benefitId}
          applicationDate={applicationDate}
          programYear={programYear}
          energyType={details.energyType}
          usedBackup={details.usedBackup}
          recordedGasUsage={details.gasUsage}
          recordedElectricUsage={details.electricUsage}
          recordedTotalUsage={details.totalUsage}
          formatMoney={fmtMoney}
        />
        <FieldGrid>
          <Field label="Used Backup" value={boolLabel(details.usedBackup)} />
          <Field label="Used Surrogate" value={boolLabel(details.usedSurrogate)} />
          <Field label="Past Due Bill Forgiveness" value={boolLabel(details.isInterestedInPastDueBillForgiveness)} />
          <Field label="2-Year Recert Opt-in Status" value={enumLabel(twoYearCertStateLabels, twoYearCertState)} />
        </FieldGrid>

      </CardContent>
    </Card>
  )
}

function SheapDetailsSection({ details }: { details: SheapBenefitDto }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <IconDroplet className="size-5" />
          <CardTitle className="text-base">SHEAP Details</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <FieldGrid>
          <Field label="Heat Source" value={details.heatSource !== null ? enumLabel(heatingEnergyLabels, details.heatSource) : "—"} />
          <Field label="Number of Bedrooms" value={fmt(details.numberOfBedrooms)} />
          <Field label="Furnace Older Than 5 Years" value={boolLabel(details.isFurnaceOlderThan5Years)} />
          <Field label="Furnace Heats Home" value={boolLabel(details.isFurnaceUsedToHeatTheHome)} />
          <Field label="Heat with Rent" value={boolLabel(details.heatWithRent)} />
          <Field label="Previous Assistance" value={boolLabel(details.previousAssistance)} />
          <Field label="Previous Assistance Amount" value={fmtMoney(details.previousAssistanceAmount)} />
          <Field label="Conservation Education" value={boolLabel(details.conservationEducation)} />
          <Field label="Used Backup" value={boolLabel(details.usedBackup)} />
          <Field label="Used Surrogate" value={boolLabel(details.usedSurrogate)} />
        </FieldGrid>
      </CardContent>
    </Card>
  )
}

// ── Main Page Component ───────────────────────────────────────────────

function VersionedWorkflowPanel({
  benefit,
  workflow,
  onReload,
  onUpdated,
}: {
  benefit: BenefitDetailDto
  workflow: BenefitWorkflowResponse | null
  onReload: () => void
  onUpdated: (updated: BenefitDetailDto) => void
}) {
  const { id: benefitId, concurrencyToken } = benefit
  const lifecycle = <LifecycleActions benefit={benefit} available={workflow?.authority === "Legacy"}
    onTransitionComplete={updated => { onUpdated(updated); onReload() }} />
  const [editing, setEditing, workflowToken] = useBenefitEdit(concurrencyToken, "workflow")
  const [action, setAction] = useBenefitDraftState("workflow", "action", "transition")
  const [target, setTarget] = useBenefitDraftState("workflow", "target", "")
  const [reasonKey, setReasonKey] = useBenefitDraftState("workflow", "reasonKey", "")
  const [explanation, setExplanation] = useBenefitDraftState("workflow", "explanation", "")
  const [saving, setSaving] = useBenefitSaving()
  const review = useBenefitReviewActive()
  const hasOpenEditor = useHasOpenBenefitEditors()
  const host = useRef<HTMLDivElement | null>(null)
  const heading = useRef<HTMLDivElement | null>(null)
  const origin = useRef<HTMLElement | null>(null)

  const v2 = workflow?.authority === "V2" && !workflow.display?.readUnavailable
  const legacy = workflow?.authority === "Legacy" && !workflow.display?.readUnavailable
  // Ambiguous rows cannot confer authority or silently choose a different reason.
  const enabled = v2 ? workflow.transitions.filter(row => row.enabled && workflow.transitions.filter(other => other.state === row.state).length === 1) : []
  const selected = enabled.find(row => row.state === target)
  const candidates = v2 ? workflow.reasons.filter(reason => reason.group === workflowReasonGroup(target)) : []
  const reasons = candidates.filter(row => candidates.filter(other => other.groupValue === row.groupValue && other.code === row.code).length === 1)
  const reason = reasons.find(row => `${row.groupValue}:${row.code}` === reasonKey)
  const canRevert = v2 && workflow.revertToProcessing?.enabled === true
  const canAdopt = legacy && workflow.adoptionEnabled
  const canProof = v2 && workflow.canProofDenial
  const available = action === "transition" ? !!selected && (!reasonKey || !!reason)
    : action === "adoption" ? canAdopt : action === "revert" ? canRevert : canProof
  const completionBlockedReason = workflow?.transitions.find(transition =>
    transition.state === "Completed" && transition.code === "identity-review-pending")?.explanation
  const confirmation = action === "adoption" ? "Confirm selective adoption" : action === "revert" ? "Confirm Revert to Processing"
    : action === "denial-proof" ? "Confirm denial is proofed (no award)" : "Apply workflow action"

  const begin = (choice: typeof action) => {
    if (saving || review || hasOpenEditor) return
    origin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setAction(choice); setTarget(choice === "transition" ? enabled[0]?.state ?? "" : ""); setReasonKey(""); setExplanation("")
    setEditing(true)
  }
  const returnFocus = (container: HTMLDivElement | null, cancelled: boolean) => requestAnimationFrame(() => {
    // No focus work from an unmounted command, including after a new host mounts.
    if (!container?.isConnected || host.current !== container) return
    const opener = container.querySelector<HTMLButtonElement>(`[data-workflow-action="${action}"]`)
    if (cancelled && origin.current?.isConnected && !origin.current.matches(":disabled") && !origin.current.closest("[hidden], [inert]")) origin.current.focus()
    else if (cancelled && opener && !opener.disabled) opener.focus()
    else heading.current?.focus()
  })
  const cancel = () => { if (!saving && !review && setEditing(false)) returnFocus(host.current, true) }
  const save = async () => {
    // Recheck current capabilities in the handler, but never adopt their root token.
    if (!editing || saving || review || !available || !workflowToken) return
    if (action === "transition" && selected?.requiresReason && !reason) {
      toast.error("Select a workflow reason."); return
    }
    if (action === "transition" && (selected?.requiresExplanation || reason?.requiresExplanation) && !explanation.trim()) {
      toast.error("An explanation is required."); return
    }
    const container = host.current
    setSaving(true)
    try {
      await runCorrectionAction(
        () => api.post<BenefitDetailDto>(`/api/benefitapplications/${benefitId}/workflow/${action === "transition" ? "transitions" : action}`,
          action === "transition" ? { state: target, reasonGroup: reason?.group, reasonCode: reason?.code,
            explanation: explanation || undefined, concurrencyToken: workflowToken } : { concurrencyToken: workflowToken }),
        onUpdated, onReload,
      )
      setEditing(false)
      returnFocus(container, false)
      if (action === "revert") toast.success("Application reverted to Processing")
      else if (action === "transition") toast.success(`${selected?.label ?? target} completed successfully`)
    } catch (err) {
      if (handledWorkspaceFailure(benefitId, err)) return
      toast.error(err instanceof Error ? err.message : "The workflow action was rejected.")
    } finally { setSaving(false) }
  }

  return (
    <Card ref={host} data-testid="workflow-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2"><IconShield className="size-5" /><CardTitle ref={heading} tabIndex={-1} className="text-base" data-workflow-heading>
          {legacy ? "Compatibility workflow" : `Application workflow: ${workflow?.display?.label ?? "Workflow unavailable"}`}
        </CardTitle></div>
        <CardDescription>{legacy
          ? "This file uses the earlier workflow. Newer actions such as Revert are not yet available here. Its existing status and history are preserved; moving it to the newer workflow requires an explicit, authorized conversion, not a change based on file age."
          : <>Server capabilities control transitions. Grant review, delivery readback, and accounting totals remain independent; legacy status {workflow?.legacyProofingStatus} is compatibility output only.</>}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {lifecycle}
        {!workflow && <p role="status" className="text-sm text-muted-foreground">Workflow capabilities are unavailable or loading. Editing stays disabled until authority is known.</p>}
        {workflow && !workflow.financialIntegrationEnabled && <p className="text-sm text-muted-foreground">{workflow.financialDisabledReason}</p>}
        {canAdopt ? <p className="text-sm text-muted-foreground">This draft is eligible to move to the replacement workflow as Processing. Its original status and history are preserved; incompatible legacy clients become read-only. Review and confirm the move explicitly.</p>
          : legacy && <p className="text-sm text-muted-foreground">{workflow.adoptionDisabledReason}</p>}
        {!editing && <div className="flex flex-wrap gap-2">
          {canAdopt && <Button data-workflow-action="adoption" disabled={saving || hasOpenEditor} onClick={() => begin("adoption")}>Review adoption</Button>}
          {enabled.length > 0 && <Button data-workflow-action="transition" disabled={saving || hasOpenEditor} onClick={() => begin("transition")}>Review workflow action</Button>}
          {canProof && <Button data-workflow-action="denial-proof" disabled={saving || hasOpenEditor} onClick={() => begin("denial-proof")}>{enabled.length > 0 ? "Review denial proof" : "Review workflow action"}</Button>}
          {canRevert && <Button data-workflow-action="revert" variant="outline" disabled={saving || hasOpenEditor} onClick={() => begin("revert")}>Revert to Processing</Button>}
        </div>}
        {workflow?.state === "Completed" && !canRevert && workflow.revertToProcessing?.explanation && <p className="text-sm text-muted-foreground">Revert unavailable: {workflow.revertToProcessing.explanation}</p>}
        {canRevert && <p className="text-sm text-muted-foreground">Revert is available for unprotected Completed applications with no grants or only Created grants. Draft amounts and vendor payments are preserved; committed review, delivery, payment, or certification evidence remains protected.</p>}
        {completionBlockedReason && <p className="text-sm text-amber-700 dark:text-amber-300">{completionBlockedReason}</p>}
        {editing && <div className="space-y-3" data-testid="workflow-editor">
          {!available && <p role="status" className="text-sm">This workflow action or selected reason is not currently available. Your original choice, fields and token are retained; cancel or refresh the saved data.</p>}
          {action === "transition" && <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Select value={target} disabled={saving || review || !enabled.length} onValueChange={value => { setTarget(value); setReasonKey(""); setExplanation("") }}>
                <SelectTrigger aria-label="Workflow transition"><SelectValue placeholder="Choose an action" /></SelectTrigger>
                <SelectContent>{target && !selected && <SelectItem value={target} disabled>{target} (unavailable)</SelectItem>}{enabled.map(transition => <SelectItem key={transition.state} value={transition.state}>{transition.label}</SelectItem>)}</SelectContent>
              </Select>
              {(selected?.requiresReason || reasonKey) && <Select value={reasonKey} disabled={saving || review || !selected} onValueChange={setReasonKey}>
                <SelectTrigger aria-label="Workflow reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                <SelectContent>{reasonKey && !reason && <SelectItem value={reasonKey} disabled>Retained reason {reasonKey} (unavailable)</SelectItem>}{reasons.map(reason => <SelectItem key={`${reason.groupValue}:${reason.code}`} value={`${reason.groupValue}:${reason.code}`}>{reason.meaning}</SelectItem>)}</SelectContent>
              </Select>}
            </div>
            <Input value={explanation} disabled={saving || review} onChange={event => setExplanation(event.target.value)} placeholder="Explanation when required" aria-label="Workflow explanation" />
          </>}
          <div className="flex flex-wrap gap-2">
            <Button variant={action === "revert" ? "destructive" : "default"} onClick={save} disabled={saving || review || !available || !workflowToken}>{saving && <IconLoader2 className="size-4 mr-1.5 animate-spin" />}{confirmation}</Button>
            <Button variant="outline" disabled={saving || review} onClick={cancel}>Cancel</Button>
          </div>
        </div>}
        {!editing && v2 && enabled.length === 0 && !canProof && !canRevert && !completionBlockedReason && <p className="text-sm text-muted-foreground">No enabled V2 transitions are available for this role and state.</p>}
        {workflow?.transitions.some(row => !row.enabled && row.code !== "transition-not-allowed") && <details className="text-sm">
          <summary>Why other actions are unavailable</summary>
          <ul className="mt-2 space-y-2">{workflow.transitions.filter(row => !row.enabled && row.code !== "transition-not-allowed").map((row, index) => <li key={`${row.state}-${index}`}>
            <span className="font-medium">{row.label}</span>: {row.explanation ?? row.code}
          </li>)}</ul>
        </details>}
        {!!workflow?.history?.length && <details className="text-sm">
          <summary>Workflow history (including return explanations)</summary>
          <ul className="mt-2 space-y-2">{workflow.history.map((item, index) => <li key={index}>
            <span className="font-medium">{item.from} → {item.to}</span> · {fmtDateTime(item.occurredAt)}
            {item.reasonCode != null && <span> · {workflow.reasons.find(reason => reason.group === item.reasonGroup && reason.code === item.reasonCode)?.meaning ?? `Reason ${item.reasonGroup}/${item.reasonCode}`}</span>}
            {item.explanation && <p className="text-muted-foreground">{item.explanation}</p>}
          </li>)}</ul>
        </details>}
      </CardContent>
    </Card>
  )
}

export default function BenefitDetailPage() {
  const { id: routeId } = useParams<{ id: string }>()
  // .NET serializes GUIDs in lowercase. Normalize pasted URLs before keying the read,
  // workflow authority and editor scope; casing must not look like a different application.
  const id = routeId?.toLowerCase()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeSection = benefitSectionTab(searchParams.get("section"))
  const historyScope = benefitHistoryScope(searchParams)
  const navigateSection = (section: string) => setSearchParams(current => withBenefitSection(current, section))
  const permissions = useAppSelector((state) => state.permissions.permissions)
  const read = useBenefitReadBridge(id)
  const { benefit, loading, error, refresh, providerNames, energyServiceOptions, vendorNames,
    selectableGrantPrograms, selectableGrantProgramsLoadFailed, selectableVendors } = read
  // Command responses never splice partial DTOs into the canonical snapshot.
  const setBenefit = useCallback((_updated?: BenefitDetailDto) => refresh(), [refresh])
  // All three parts come from the same server read; original write tokens remain opaque.
  const workflowAuthority = benefit?.id === id ? read.workflow : null

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <IconLoader2 className="size-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading benefit application…</p>
      </div>
    )
  }

  if (error || !benefit) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <p className="text-sm text-destructive">{error ?? "Benefit not found"}</p>
        <Button variant="outline" size="sm" onClick={refresh}>Retry read</Button>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
          <IconArrowLeft className="size-4 mr-1" />
          Go Back
        </Button>
      </div>
    )
  }

  const legacyCanEdit = isBenefitEditable(benefit.proofingStatus, permissions.includes("benefit-applications:edit"))
  const canEdit = (section: string) => workflowCanEdit(workflowAuthority, section, legacyCanEdit)
  const canEditApplication = canEdit("Household")
  const canAssignOwnership = read.current && canReassignBenefitOwnership(permissions)
  const applicant = benefit.personCollection.find((p) => p.relationship === 0)
  const applicantName = applicant ? formatPersonName(applicant) : "Unknown Applicant"

  // Benefit-type tags: distinct programs from grants, falling back to the file's
  // primary benefitApplicationType when no grants exist yet. Primary type first,
  // remainder sorted by program label.
  const primaryAppTypeLabel = benefitAppTypeLabels[benefit.benefitApplicationType]
    ?? `Type ${benefit.benefitApplicationType}`
  const grantProgramIds = Array.from(
    new Set(benefit.benefitGrants.map((g) => g.benefitProgramInstance.program))
  )
  const grantProgramLabels = grantProgramIds.map(getBenefitProgramLabel)
  const helpCapable = benefit.benefitApplicationType === 0 || grantProgramIds.includes(0) ||
    benefit.requestedBenefits.some(program => program.programId === 0)
  const helpPrintYears = Array.from(new Set([
    ...benefit.benefitGrants.filter(grant => grant.benefitProgramInstance.program === 0).map(grant => grant.benefitProgramInstance.year),
    ...benefit.benefitEligibility.filter(item => item.benefitProgramInstance.program === 0).map(item => item.benefitProgramInstance.year),
  ]))
  const benefitTypeTags = grantProgramLabels.length > 0
    ? Array.from(new Set([
        ...grantProgramLabels.filter((l) => l === primaryAppTypeLabel || l === `${primaryAppTypeLabel} Help`),
        ...grantProgramLabels.filter((l) => l !== primaryAppTypeLabel && l !== `${primaryAppTypeLabel} Help`).sort(),
      ]))
    : [primaryAppTypeLabel]

  return (
    <BenefitEditorWorkspace key={benefit.id} benefitId={benefit.id} readStamp={read.readStamp} writable={read.current} onRefresh={refresh}>
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {!read.current && !read.refreshUnavailable && <p role="status" className="text-sm text-muted-foreground">Refreshing saved application. Save actions are temporarily unavailable.</p>}
      {read.refreshUnavailable && <div role="status" className="rounded border p-3 text-sm">
        Saved-state refresh is unavailable. The last read is shown read-only; a confirmed save will not be resent.
        <Button variant="outline" size="sm" className="ml-2" onClick={refresh}>Retry refresh</Button>
      </div>}
      {read.catalogUnavailable && <p role="status" className="text-sm text-muted-foreground">Some reference options are unavailable. Missing options are not authoritative empty results.</p>}
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate(-1)} className="mt-0.5">
          <IconArrowLeft className="size-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <IconUser className="size-6" />
            <h1 className="text-2xl font-bold truncate">{applicantName}</h1>
            <WorkflowStatusBadge workflow={workflowAuthority} />
            {benefitTypeTags.map((label) => (
              <Badge key={label} variant="outline">{label}</Badge>
            ))}
            {workflowAuthority?.authority === "V2" && workflowAuthority.pledgeGenerations.length > 0 && (
              <Badge variant="outline" aria-label="Pledge generations">
                Pledges: {workflowAuthority.pledgeGenerations.filter((generation) => generation.isCurrent).map((generation) => generation.status).join(", ")}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Benefit Application{benefit.fileNumber ? ` • File ${benefit.fileNumber}` : ""} • Application {fmtDate(benefit.applicationDate)}
            {benefit.certificationDate ? ` • Certified ${fmtDate(benefit.certificationDate)}` : ""}
            {` • Intake owner: ${ownerDisplay(benefit.ownership)}`}
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          {helpCapable && benefit.pseBenefitDetails && permissions.includes("people:full-identifiers") &&
            <BenefitPrintActions key={benefit.id} benefitId={benefit.id} programYears={helpPrintYears} />}
          <Button variant="outline" onClick={() => navigateSection("history-admin")}
            aria-label="Open workflow in History/Admin">Workflow</Button>
        </div>
      </div>

      {/* Inactive panels may unmount; Redux owns drafts, tokens and attempts. */}
      <Tabs value={activeSection} onValueChange={navigateSection} className="application-section-tabs">
        <div className="overflow-x-auto pb-1">
          <TabsList variant="line" aria-label="Benefit application sections" className="application-section-list w-max min-w-full">
            {BENEFIT_SECTION_TABS.map(section => (
              <TabsTrigger key={section} value={section}>{BENEFIT_SECTION_LABELS[section]}</TabsTrigger>
            ))}
          </TabsList>
        </div>
      <div className="space-y-4">
        <TabsContent value="overview" className="space-y-4">
          <HouseholdCopyNotice benefit={benefit} />
          <ApplicationOverview key={benefit.id} benefit={benefit} workflow={workflowAuthority}
            canEditContacts={canEditApplication} onNavigate={navigateSection}>
            <RecordedApplicationDetails b={benefit} workflow={workflowAuthority} />
          </ApplicationOverview>
        </TabsContent>
        <TabsContent value="household" className="space-y-4">
          <HouseholdDetails key={benefit.id} benefit={benefit} onProgramDetails={() => navigateSection("program-details")}
            editDisabledReason={householdEditDisabledReason({ current: read.current, refreshUnavailable: read.refreshUnavailable, workflow: workflowAuthority, canEdit: canEditApplication })}
            // The questionnaire needs Household AND Program, so it explains whichever one refuses.
            questionnaireDisabledReason={householdEditDisabledReason({ current: read.current, refreshUnavailable: read.refreshUnavailable, workflow: workflowAuthority, canEdit: canEditApplication })
              ?? householdEditDisabledReason({ current: read.current, refreshUnavailable: read.refreshUnavailable, workflow: workflowAuthority, canEdit: canEdit("Program"), section: "Program" })}
            onHistory={() => navigateSection("history-admin")}
            contact={<IntakeDetailsSection benefit={benefit} canEdit={canEditApplication} canEditProgram={canEdit("Program")} onUpdated={setBenefit} />}
            addresses={<ApplicationDetailsSection benefit={benefit} canEdit={canEditApplication} onUpdated={setBenefit} />}
            housing={<HouseholdInformationSection benefit={benefit} canEdit={canEditApplication} onUpdated={setBenefit} />}
            nonCash={<NonCashBenefitsSection benefit={benefit} canEdit={canEditApplication} onUpdated={setBenefit} />}
            questionnaire={<QuestionnaireSection benefit={benefit} canEdit={canEditApplication && canEdit("Program")} onUpdated={setBenefit} />} />
        </TabsContent>
        <TabsContent value="demographics">
          <DemographicsDetails key={benefit.id} benefit={benefit} correction={<ApplicantHouseholdCorrection benefit={benefit} workflow={workflowAuthority} onUpdated={setBenefit} />}>
            <HouseholdMembersSection benefit={benefit} canEdit={canEdit("Demographics")} onUpdated={setBenefit} onHistory={() => navigateSection("history-admin")} />
          </DemographicsDetails>
        </TabsContent>
        <TabsContent value="income-eligibility" className="space-y-4">
          <HouseholdIncomeSection key={benefit.id} benefit={benefit} canEdit={canEdit("Income")} onUpdated={setBenefit}
            onHistory={() => navigateSection("history-admin")}
            onProgram={() => navigateSection("program-details")} />
        </TabsContent>
        <TabsContent value="program-details" className="space-y-4">
        <ProgramDetails benefit={benefit} providerNames={providerNames} energyServiceOptions={energyServiceOptions}
          onManageAwards={() => navigateSection("grants-payments")}
          onHousehold={() => navigateSection("household")}
          editDisabledReason={householdEditDisabledReason({ current: read.current, refreshUnavailable: read.refreshUnavailable,
            workflow: workflowAuthority, canEdit: canEdit("Program"), section: "Program" })}
          targetYear={<TargetProgramYearSection benefit={benefit} canEdit={canEdit("Program")} onUpdated={setBenefit} />}
          requests={<RequestedBenefitsSection benefit={benefit} canEdit={canEdit("Program")} onUpdated={setBenefit} />}
          bill={<EnergyBillInformationSection benefit={benefit} providerNames={providerNames}
            energyServiceOptions={energyServiceOptions} canEdit={canEdit("Program")} onUpdated={setBenefit} />}
          intake={<EnergyIntakeSection benefit={benefit} canEdit={canEdit("Program")} onUpdated={setBenefit} />}>
        {benefit.liheapBenefitDetails && (
          <LiheapDetailsSection
            details={benefit.liheapBenefitDetails}
            providerNames={providerNames}
            twoYearCertState={benefit.twoYearCertificationState}
            receivedEapLastYear={benefit.recvdEaplastYear}
          />
        )}
        {benefit.pseBenefitDetails && (
          <PseDetailsSection
            benefitId={benefit.id}
            applicationDate={benefit.applicationDate}
            programYear={benefit.targetProgramYear?.effectiveProgramYear}
            details={benefit.pseBenefitDetails}
            twoYearCertState={benefit.twoYearCertificationState}
          />
        )}
        {benefit.sheapBenefitDetails && <SheapDetailsSection details={benefit.sheapBenefitDetails} />}

        </ProgramDetails>
        </TabsContent>
        <TabsContent value="grants-payments" className="grant-workspace-panel">
        <GrantsSection
          benefit={benefit}
          vendorNames={vendorNames}
          selectablePrograms={selectableGrantPrograms}
          selectableProgramsLoadFailed={selectableGrantProgramsLoadFailed}
          selectableVendors={selectableVendors}
          workflow={workflowAuthority}
          permissions={permissions}
          canEdit={workflowAuthority?.authority === "V2" && !workflowAuthority.display?.readUnavailable
            ? workflowAuthority.grantAuthoringEnabled && workflowCanEdit(workflowAuthority, "VendorPayment", false)
            : workflowAuthority?.authority === "Legacy"
              ? isGrantEntryEditable(benefit.proofingStatus, permissions.includes("benefit-applications:edit"))
              : false}
          onUpdated={setBenefit}
          onReload={refresh}
        />
        <section className="grant-support" aria-labelledby="grant-support-heading">
          <header><h2 id="grant-support-heading">Eligibility & financial details</h2><p>Calculate eligibility and review the supporting financial records for this application.</p></header>
          <EligibilityCalculationSection
            benefit={benefit}
            canEdit={workflowAuthority?.authority === "Legacy" ? canEditApplication : workflowCanEdit(workflowAuthority, "Program", false)}
            onUpdated={setBenefit}
          />
          <EligibilitySection eligibility={benefit.benefitEligibility} />
          <FinancialSection b={benefit} />
        </section>
        </TabsContent>
        <TabsContent value="history-admin" className="space-y-4">
        <ApplicationHistory key={benefit.id} benefit={benefit} workflow={workflowAuthority}
          scope={historyScope} onScopeChange={scope => setSearchParams(current => withBenefitHistoryScope(current, scope))}>
        <AdministrativeDataSection b={benefit} workflow={workflowAuthority} providerNames={providerNames}
          energyServiceOptions={energyServiceOptions} />
        <VersionedWorkflowPanel
          key={benefit.id}
          benefit={benefit}
          workflow={workflowAuthority}
          onReload={refresh}
          onUpdated={setBenefit}
        />
        <BenefitOwnershipCard
          benefit={benefit}
          canAssign={canAssignOwnership}
          onUpdated={() => refresh()}
          onReload={setBenefit}
        />
        {workflowAuthority?.authority === "V2" && <PledgeInstructionHistorySection
          benefit={benefit}
          generations={workflowAuthority.pledgeGenerations}
          canEdit={workflowAuthority.pledgeEffectsEnabled && workflowCanEdit(workflowAuthority, "VendorPayment", false)}
        />}
        </ApplicationHistory>
        </TabsContent>
      </div>
      </Tabs>

      <BackToTopButton />
    </div>
    </BenefitEditorWorkspace>
  )
}
